export type AiFlowChannel =
  | "all"
  | "skills"
  | "commerce"
  | "chat"
  | "comment"
  | "question"
  | "privateReply"

export type AiFlowPromptRef = {
  name: string
  usage: string
  placeholders?: {
    token: string
    meaning: string
  }[]
}

export type AiFlowNodeKind =
  | "platform"
  | "context"
  | "guard"
  | "prompt"
  | "decision"
  | "action"
  | "rag"
  | "skill"
  | "workflow"
  | "mcp"
  | "security"
  | "transaction"
  | "audit"
  | "output"

export type AiFlowNodeInfo = {
  id: string
  title: string
  shortTitle?: string
  subtitle: string
  kind: AiFlowNodeKind
  stage: string
  productSummary: string
  details: string[]
  inputs: string[]
  outputs: string[]
  decisionOutcomes?: string[]
  promptNames?: AiFlowPromptRef[]
  codeRefs: string[]
  channels: AiFlowChannel[]
  position: { x: number; y: number }
}

export type AiFlowEdgeInfo = {
  id: string
  source: string
  target: string
  label?: string
  channels?: AiFlowChannel[]
  /**
   * The "hot" edges of a lane — the hops that actually carry a model decision or a remote call.
   * Declared here rather than matched on the label text in the page, so renaming a label can no
   * longer silently stop an edge from animating.
   */
  animated?: boolean
}

export const AI_FLOW_CHANNEL_OPTIONS: {
  value: AiFlowChannel
  label: string
}[] = [
  { value: "all", label: "Tüm Akış" },
  { value: "skills", label: "Skills" },
  { value: "commerce", label: "Commerce / MCP" },
  { value: "chat", label: "Chat / Email" },
  { value: "comment", label: "Comment" },
  { value: "question", label: "Marketplace Question" },
  { value: "privateReply", label: "Private Reply" }
]

export const AI_FLOW_NODES: AiFlowNodeInfo[] = [
  {
    id: "incoming-message",
    title: "Mesaj AI kuyruğuna gelir",
    shortTitle: "Incoming message",
    subtitle: "Yeni müşteri mesajı sonrası AI cevap süreci başlar.",
    kind: "platform",
    stage: "Platform",
    productSummary:
      "Kanaldan gelen mesaj konuşmaya bağlanır; audit run, typing status ve AI cevap süreci tetiklenir.",
    details: [
      "Yeni mesaj konuşma ve kişi kanalıyla eşleştirilir.",
      "AI cevap verecek mi kararı BİRLEŞİK kapıdır: müşterinin rollerinden herhangi biri bu kanal, mesaj tipi ve çalışma saati için uygunsa AI devreye girer.",
      "Her yüzey rolün kendi ayar bloğundan okunur: chat kanalları `chatSettings`, ticket olmayan e-posta kendi `emailSettings` bloğu (yalnız Servis ve E-ticaret rolünde), e-posta ticket'ı (`isTicket`) `ticketSettings`, yorum `commentSettings`, marketplace sorusu `marketPlaceSettings`. `emailSettings`'i henüz olmayan profilde e-posta, `MIGRATE_AI_EMAIL_SETTINGS` çalışana kadar `chatSettings` ile cevaplanır.",
      "AI aktifse audit run başlatılır ve kullanıcıya typing status gösterilir.",
      "Asıl cevap üretimi `handleReplyByAi` içinde yürür ve sırası sabittir: enrichment → Skills → commerce resume → genel intent pipeline → finalize."
    ],
    inputs: [
      "Kanal mesajı",
      "Conversation",
      "Person channel",
      "Unique queue key"
    ],
    outputs: ["AI run id", "Typing status", "handleReplyByAi çağrısı"],
    codeRefs: [
      "api-v2/app/src/modules/Message/services/platform.service.ts:769",
      "api-v2/app/src/modules/Message/services/platform.service.ts:785"
    ],
    channels: [
      "all",
      "skills",
      "commerce",
      "chat",
      "comment",
      "question",
      "privateReply"
    ],
    position: { x: 0, y: 120 }
  },
  {
    id: "profile-memory",
    title: "AI profil ve hafıza hazırlanır",
    shortTitle: "Profile + memory",
    subtitle: "AI kimliği, önceki konuşma ve görsel context toplanır.",
    kind: "context",
    stage: "Platform",
    productSummary:
      "AI'nin hangi persona ile cevap vereceği, daha önce ne konuşulduğu ve bekleyen aksiyon olup olmadığı belirlenir.",
    details: [
      "Müşterinin AI profili ve AI customer person kaydı bulunur veya oluşturulur.",
      "Kullanıcı mesajı AI memory'ye eklenir; önceki konuşma ChatGPT mesaj formatına çevrilir.",
      "Platform katmanı chat/email için mevcut mesaj hariç geçmişi, comment/question/private reply için son memory item'ını hazırlar.",
      "Pipeline katmanı comment/question/private reply tiplerini single-turn gördüğü için LLM previousMessages alanını boş kullanır; kanal bağlamı `extraData` ve `{{data}}` üzerinden gider.",
      "Bu v1 davranışıdır. v2'de de marketplace sorusu tek ve kendi başına yeterli bir cevapla karşılanır (tek cevap hakkı, oturum geçmişi tekrar oynatılmaz), ama ajan aynı alıcının AYNI ilan/ürün/soru thread'indeki önceki sorularını ve cevaplarını tur bağlamı olarak görür: aynı konuşmanın `messageType: QUESTION` mesajları, son 30 gün, en fazla 5, başka ürünlerin soruları hariç. Devam soruları böylece anlaşılır.",
      "Görsel context ve bekleyen aksiyon source bilgisi sonraki promptlara taşınır.",
      "Hangi geçmiş mesajların kullanılacağı artık enrich adımındaki bağlam çözümlemesiyle seçilir; konu değişimi (NEW_TOPIC) tespit edilirse geçmiş tamamen göz ardı edilir."
    ],
    inputs: [
      "AI profile",
      "Conversation history",
      "Message media",
      "Waiting action state"
    ],
    outputs: [
      "Previous messages",
      "Available image contexts",
      "Pending action source"
    ],
    codeRefs: [
      "api-v2/app/src/modules/Message/services/platform.service.ts:1240",
      "api-v2/app/src/modules/Message/services/platform.service.ts:1285",
      "api-v2/app/src/modules/Message/services/platform.service.ts:1404",
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:1440"
    ],
    channels: [
      "all",
      "skills",
      "commerce",
      "chat",
      "comment",
      "question",
      "privateReply"
    ],
    position: { x: 360, y: 120 }
  },
  {
    id: "start-routing",
    title: "Başlangıç tipi seçilir",
    shortTitle: "Entry routing",
    subtitle: "Mesaj tipi ilk prompt ailesini belirler.",
    kind: "decision",
    stage: "Routing",
    productSummary:
      "Sistem önce kullanıcının hangi iş akışından geçtiğini seçer: devam eden aksiyon, yorum, soru, özel yanıt veya normal chat.",
    details: [
      "Kullanıcıdan eksik bilgi bekleniyorsa doğrudan aksiyon tespiti yapılır.",
      "Yorum, marketplace question ve private reply kendi intent promptlarına gider.",
      "Chat ve email varsayılan olarak chat intent promptuyla başlar."
    ],
    inputs: ["Message type", "waitingForAnswer", "Product metadata"],
    outputs: ["AskQuestion type", "Product details for marketplace question"],
    decisionOutcomes: [
      "waitingForAnswer -> ACTION_DETECTION",
      "COMMENT -> INTENT_DETECTION_COMMENT",
      "QUESTION -> product detail + INTENT_DETECTION_QUESTION",
      "PRIVATE_REPLY -> INTENT_DETECTION_PRIVATE_REPLY",
      "Chat/email -> INTENT_DETECTION_CHAT"
    ],
    codeRefs: [
      "api-v2/app/src/modules/Message/services/platform.service.ts:1349",
      "api-v2/app/src/modules/Message/services/platform.service.ts:1382",
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:137"
    ],
    channels: [
      "all",
      "skills",
      "commerce",
      "chat",
      "comment",
      "question",
      "privateReply"
    ],
    position: { x: 720, y: 120 }
  },
  {
    id: "question-product",
    title: "Marketplace ürün bilgisi eklenir",
    shortTitle: "Product detail",
    subtitle:
      "Question mesajında ürün başlığı, açıklaması ve attribute'lar context olur.",
    kind: "context",
    stage: "Context",
    productSummary:
      "Marketplace soru cevaplarında AI yalnızca kullanıcı metnini değil, ürün kartındaki bilgileri de görür.",
    details: [
      "Trendyol, Hepsiburada veya N11 ürün detayı okunur.",
      "Ürün başlığı, açıklaması ve attribute listesi `extraData.description` alanına yazılır.",
      "RAG promptlarında bu bilgi `{{product_details}}` veya `{{data}}` yerine geçer."
    ],
    inputs: ["Product main id", "SKU", "Platform type", "Product name"],
    outputs: ["Structured product details", "Question-specific context"],
    codeRefs: [
      "api-v2/app/src/modules/Message/services/platform.service.ts:1382",
      "api-v2/app/src/modules/QpienAi/services/qpienAi.service.ts:438"
    ],
    channels: ["all", "question"],
    position: { x: 720, y: -180 }
  },
  {
    id: "handle-ask-question",
    title: "Qpien AI pipeline çağrılır",
    shortTitle: "handleAskQuestion",
    subtitle: "Platform context'i AI pipeline parametrelerine dönüşür.",
    kind: "platform",
    stage: "Pipeline",
    productSummary:
      "Mesaj, kişi bilgisi, geçmiş, ürün bilgisi, görsel context ve kanal ayarları tek bir AI isteğine çevrilir.",
    details: [
      "`handleReplyByAi`, `qpienAiService.handleAskQuestion` çağırır.",
      "Sıra sabittir: (1) turn enrichment başlatılır, (2) Skills yönlendirmesi denenir, (3) aktif commerce workflow'u devam ettirilmeye çalışılır, (4) hiçbiri sahiplenmezse genel `askQuestion` pipeline'ı çalışır, (5) gerekiyorsa final promptu cevabı revize eder.",
      "Skills veya commerce turu sahiplenirse genel intent pipeline'ı hiç çalışmaz.",
      "Spam, içeriksiz insan desteği ve `NO_RESPONSE` sonuçları final promptuna gitmeden çıkar.",
      "`PRESERVE_EXACT` cevaplar finalize'ı atlar ama cevap veren rolü kendisi çözer, çünkü ücretlendirme o role yazılır.",
      "`deferFinalize` ile bir koordinatör (comment + auto private reply) cevabı alıp finalize'ı kendisi tetikleyebilir."
    ],
    inputs: [
      "Query",
      "Previous messages",
      "Extra data",
      "Provisional AI profile",
      "Eligible AI profiles",
      "Run id"
    ],
    outputs: [
      "IAiAnswerData",
      "Finalized answer when applicable",
      "resolvedAiRole"
    ],
    codeRefs: [
      "api-v2/app/src/modules/Message/services/platform.service.ts:1419",
      "api-v2/app/src/modules/QpienAi/services/qpienAi.service.ts:1534",
      "api-v2/app/src/modules/QpienAi/services/qpienAi.service.ts:1754"
    ],
    channels: [
      "all",
      "skills",
      "commerce",
      "chat",
      "comment",
      "question",
      "privateReply"
    ],
    position: { x: 1080, y: 120 }
  },
  {
    id: "guard",
    title: "Quota ve boş mesaj kontrolü",
    shortTitle: "Guard",
    subtitle: "AI cevap üretmeye uygun mu?",
    kind: "guard",
    stage: "Pipeline",
    productSummary:
      "Müşteri token/bakiye hakkı yoksa veya mesaj boşsa AI insan desteğine düşer.",
    details: [
      "Token/bakiye yeterliliği kontrol edilir (`controlAiEnoughToken`).",
      "Metin yoksa ve görsel de yoksa cevap üretilmez.",
      "Guard başarısızsa süreç `humanSupport` + `reason` ile sonlanır ve audit'e `GUARD_STAGE` adımı yazılır: `INSUFFICIENT_AI_QUOTA_OR_BALANCE` veya `EMPTY_QUERY_AND_MEDIA`.",
      "Pipeline içi transition zinciri 4 seviyeyi aşarsa aynı adım `TRANSITION_DEPTH_EXCEEDED` ile yazılır; artık hiçbir run sebebi kaydedilmeden 'sessizce' bitmez.",
      "Guard pipeline'ın ilk adımıdır ama turun ilk LLM çağrısı değildir: enrichment `handleAskQuestion` başında başlar, yani kotasız bir müşteri bir enrich çağrısı ödemiş olabilir."
    ],
    inputs: ["Customer id", "Normalized query", "Media"],
    outputs: ["Continue", "humanSupport + reason", "GUARD_STAGE audit step"],
    decisionOutcomes: [
      "Yeterli hak + mesaj var -> devam",
      "Kota/bakiye yok -> INSUFFICIENT_AI_QUOTA_OR_BALANCE",
      "Boş mesaj + görsel yok -> EMPTY_QUERY_AND_MEDIA",
      "Transition derinliği 4'ü aştı -> TRANSITION_DEPTH_EXCEEDED"
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:178",
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:256"
    ],
    channels: [
      "all",
      "skills",
      "commerce",
      "chat",
      "comment",
      "question",
      "privateReply"
    ],
    position: { x: 2520, y: 120 }
  },
  {
    id: "enrich",
    title: "Mesaj zenginleştirilir (turun ilk adımı)",
    shortTitle: "Turn enrichment",
    subtitle: "Dil, keyword, görsel ve bağlam normalizasyonu yapılır.",
    kind: "prompt",
    stage: "Prompt",
    productSummary:
      "Kullanıcının son mesajı AI'nin daha iyi anlayacağı temiz bir sorguya çevrilir; görseller özetlenir, konuşma sürekliliği sınıflandırılır. Artık turun EN BAŞINDA çalışır: Skills ve commerce resume de zenginleştirilmiş mesajı görür.",
    details: [
      "`handleAskQuestion` ilk iş olarak `startTurnEnrichment` başlatır; Skills'in ucuz yolu (iki Mongo okuması) bu çağrı uçarken döndüğü için ek gecikme yaratmaz.",
      "Rol seçiminden bağımsızdır (prompt satırı paylaşımlıdır, `aiProfile` okumaz); bu yüzden rol kararından önce çalışabilir.",
      "Tur başına TEK sefer çalışır: sonuç `withEnrichment` ile params'a yazılır, intent pipeline aynı çağrıyı ikinci kez yapmaz (`reusedEnrichedValue`). Comment + auto private reply koordinatörü de aynı sonucu iki tarafa paylaştırır.",
      "Yalnız intent pipeline'ına giren tipler zenginleştirilir (`AI_INTENT_ENTRY_TYPES`): ACTION_DETECTION takipleri, finalize ve direkt RAG çağrıları enrich ödemez.",
      "Metin mesajı için prompt satırı `ENRICH_USER_QUERY_TEXT`, görsel mesaj için `ENRICH_USER_QUERY_IMAGE` okunur; audit'te ikisi de `ENRICH_USER_QUERY` adımı olarak görünür.",
      "Metinli mesajlarda, önceki konuşma varsa, normalizasyon ile bağlam çözümleme aynı anda (`Promise.all`) çalışır; görselde önce normalizasyon biter, bağlam ondan sonra çözülür.",
      "Bağlam çözümleme ayrı bir prompt kaydı değildir: aynı prompta `ENRICH_CONTEXT_RESOLUTION_RULES` eklenir ve audit'te `CONTEXT_RESOLUTION` adımı olarak görünür.",
      "Mesaj NEW_TOPIC (bağımsız/konu değişimi), FOLLOW_UP (öncekine bağımlı), EXPECTED_INPUT (istenen bilgiyi veriyor) veya CONFIRMATION (önceki soruyu onaylıyor/reddediyor) olarak sınıflandırılır.",
      "NEW_TOPIC ise geçmişten hiçbir mesaj kullanılmaz; diğer modlarda yalnız ilgili geçmiş mesaj index'leri context'e dahil edilir.",
      "Birden fazla AI rolü uygunsa aynı çağrıya `businessRole` sınıflandırması biner (`AI_ROLE_SELECTION_RULES`); ekstra LLM turu maliyeti yoktur ve tek rollü müşteride hiç sorulmaz.",
      "Modeller kodda pinlidir, prompt satırından değiştirilemez: metin ve bağlam `gemini-3.1-flash-lite`, görsel `gpt-5.6-luna`. Görsel adımı tek seferlik fallback olarak `gemini-3.1-flash-lite` ile bir kez daha denenir — görseli okuyan tek adım olduğu için kaybı bütün sonraki adımları etkiler.",
      "Enrich bir ön koşul değil iyileştirmedir: hata alırsa null döner ve tur ham sorguyla devam eder. Yalnız metin hiç yokken (sadece görsel) ve görsel indirilemediyse run gerçekten başarısız olur.",
      "YORUM turunda gönderi konudur: `resolvePostContext` gönderi açıklamasını ve gönderi görselinin özetini (`POST_IMAGE_SUMMARY` satırı, gönderi başına TEK vision çağrısı, `PostImageSummaryCacheService` 7 gün) enrich promptuna `POST CONTEXT CONTRACT` bloğu olarak verir; 'fiyatı ne kadar' Batman çizgi roman gönderisi altında 'Batman çizgi romanının fiyatı ne kadar?' olur. Aynı `postContext` Skills tetiklemesine, tur yönlendirmesine ve agent'a (`attributes.extra.postImageSummary`) gider; RAG'in `{{product_details}}` alanı da görsel özetini taşır (run 6a9fa9bc18a03f361bc6921a). Marketplace QUESTION bilinçli olarak dışarıda: ilanı commerce lane çözer. Gecikme için: `handleNewMessage` yorumu doğrular doğrulamaz özeti arka planda başlatır (`warmPostImageSummary`), AI turu Redis'teki uçuşta işaretini görüp aynı sonucu bekler (`POST_IMAGE_SUMMARY_WAIT_MS`), ikinci çağrı yapmaz; model `minimal` efor + 10 s bütçe + gemini fallback."
    ],
    inputs: [
      "Raw query",
      "Previous messages",
      "Images",
      "Available image contexts",
      "Eligible AI roles"
    ],
    outputs: [
      "processedMessage",
      "detectedLanguage",
      "keywords",
      "imageSources",
      "contextMode",
      "usedHistoryIndexes",
      "classifiedAiRole"
    ],
    promptNames: [
      {
        name: "ENRICH_USER_QUERY_TEXT",
        usage:
          "Metin mesajını normalize eder, dil ve keyword çıkarır, rol sınıflandırmasını taşır."
      },
      {
        name: "ENRICH_USER_QUERY_IMAGE",
        usage:
          "Görselli mesajı normalize eder ve görsel context üretir; turun görseli okuyan tek adımı."
      },
      {
        name: "POST_IMAGE_SUMMARY",
        usage:
          "Yorum turunda gönderi görselinin ne gösterdiğini bir-iki cümleyle özetler; gönderi başına bir kez çalışır, önbelleklenir"
      }
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/qpienAi.service.ts:1542",
      "api-v2/app/src/modules/QpienAi/services/qpienAi.service.ts:1637",
      "api-v2/app/src/modules/QpienAi/services/qpienAi.service.ts:3021",
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:335",
      "api-v2/app/src/modules/QpienAi/core/qpienAi.constants.ts:164",
      "api-v2/app/src/modules/QpienAi/core/qpienAi.constants.ts:301"
    ],
    channels: [
      "all",
      "skills",
      "commerce",
      "chat",
      "comment",
      "question",
      "privateReply"
    ],
    position: { x: 1440, y: 120 }
  },
  {
    id: "guidance",
    title: "Guidance seçilir",
    shortTitle: "Guidance selection",
    subtitle: "Kanal ve keyword'e göre iş kuralları promptlara eklenir.",
    kind: "context",
    stage: "Prompt",
    productSummary:
      "PM'in tanımladığı iletişim tarzı, spam, açıklama isteme ve eskalasyon kuralları ilgili promptlara taşınır.",
    details: [
      "Enrich sonrası çıkan keyword'ler guidance seçimi için kullanılır.",
      "Seçim çözülen role göre yapılır: her rol kendi guidance setini görür.",
      "Seçilen guidance kayıtları kişi/agent değişkenleriyle render edilir.",
      "Intent, RAG, action ve final promptlarında ilgili placeholder'lar doldurulur.",
      "Bu adımın LLM promptu yoktur; audit'te `GUIDANCE_SELECTION` adımı olarak görünür (bir `AiPrompts` kaydı değildir)."
    ],
    inputs: [
      "Customer id",
      "Channel id",
      "Keywords",
      "Message type",
      "Resolved AI role"
    ],
    outputs: ["Selected guidances", "Rendered guidance text"],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:376",
      "api-v2/app/src/modules/QpienAi/services/qpienAi.service.ts:2858"
    ],
    channels: [
      "all",
      "skills",
      "commerce",
      "chat",
      "comment",
      "question",
      "privateReply"
    ],
    position: { x: 3240, y: 120 }
  },
  {
    id: "intent",
    title: "Niyet tespiti yapılır",
    shortTitle: "Intent detection",
    subtitle: "AI kullanıcının isteğini ana yola ayırır.",
    kind: "prompt",
    stage: "Prompt",
    productSummary:
      "Kullanıcı basit cevap mı bekliyor, bilgi tabanı mı gerekiyor, aksiyon mu çalışmalı, spam mi, insan mı devreye girmeli kararı burada alınır.",
    details: [
      "Bu adıma yalnız Skills VE commerce resume turu sahiplenmediğinde gelinir; Skill cevaplayan bir mesaj intent promptunu hiç görmez.",
      "Chat için önceki mesajlar prompta girer; comment/question/private reply tekil kanal bağlamıyla çalışır.",
      "Comment, question ve private reply single-turn kabul edilir; pipeline bu tiplerde previousMessages'ı boş listeye indirir (v2'nin marketplace sorusuna eklediği önceki soru bağlamı v1'de yok).",
      "Persona, eskalasyon, açıklama isteme ve spam guidance alanları prompta eklenir.",
      "Intent çıktısı sonraki route'u belirleyen `action` alanını döner.",
      "İlk commerce mesajı bu adıma tam bir kez gelir; aktif workflow devam mesajları enrich/intent'i atlayarak orchestrator'a döner.",
      "`basic` ve `clarify` sadece chat intent'inde direkt dönüş olabilir; comment/question/private reply direkt dönüşte yalnız `helped`, `humanSupport` ve `spam` kullanır.",
      "Hangi seçeneklerin (rag/action/commerce) LLM'e sunulacağı, müşteri/kanal/AI profiline göre cache'lenen 'intent capabilities' tarafından belirlenir; kapasite kapalıysa o seçenek modele hiç gösterilmez, yine de seçilirse insan desteğine düşer.",
      "Kapasite cache'i Redis revision sayaçlarıyla çalışır: action/knowledge source/AI profile/e-ticaret entegrasyonu değiştiğinde revision artar, bir sonraki mesaj sabit bir gecikme olmadan güncel kapasiteyi görür.",
      "`commerce` kapasitesi role bağlıdır: aktif e-ticaret entegrasyonu VE rolün commerce'e izin vermesi gerekir. Bilgi tabanı (`rag`) ise bilinçli olarak role göre daraltılmaz.",
      "Intent cevap şeması kodda üretilir (`buildIntentResponseSchema`); prompt satırlarının artık kendi `responseSchema` alanını taşıması gerekmez. Prompt metni `{{available_intents}}` ve `{{#if_rag}}` / `{{#if_action}}` / `{{#if_rag_and_action}}` / `{{#if_commerce}}` blokları ile aynı kapasite filtresine bağlanabilir.",
      "`INTENT_DETECTION_TICKET` ve `ANSWER_BY_RAG_TICKET` route olarak tanımlı ama bugün hiçbir çağıran bu tipi göndermiyor — ticket girişi ayrılmış bir yer tutucudur."
    ],
    inputs: [
      "Enriched query",
      "AI profile",
      "Guidances",
      "Channel/product context",
      "Intent capabilities (cached)"
    ],
    outputs: ["Intent action", "Intent answer", "Detected language"],
    decisionOutcomes: [
      "Chat direct: basic / helped / humanSupport / clarify",
      "Comment/question/private direct: helped / humanSupport",
      "All channels: spam",
      "All channels: rag / action / ragAndAction",
      "Commerce-enabled profile/channel: commerce"
    ],
    promptNames: [
      {
        name: "INTENT_DETECTION_CHAT",
        usage: "Chat ve email mesajlarının ana niyetini seçer.",
        placeholders: [
          {
            token: "{action_message_human_support}",
            meaning: "İnsan desteği buton mesajı"
          },
          { token: "{{persona_section}}", meaning: "AI persona ve iş kapsamı" },
          { token: "{{escalation_guidance}}", meaning: "Eskalasyon kuralları" },
          {
            token: "{{clarify_guidance}}",
            meaning: "Açıklama isteme kuralları"
          },
          { token: "{{spam_guidance}}", meaning: "Spam ayrıştırma kuralları" }
        ]
      },
      {
        name: "INTENT_DETECTION_COMMENT",
        usage: "Yorum cevabı için niyet seçer.",
        placeholders: [
          { token: "{{data}}", meaning: "Post/comment/ürün bağlamı" }
        ]
      },
      {
        name: "INTENT_DETECTION_QUESTION",
        usage: "Marketplace question niyetini seçer.",
        placeholders: [{ token: "{{data}}", meaning: "Ürün ve soru bağlamı" }]
      },
      {
        name: "INTENT_DETECTION_PRIVATE_REPLY",
        usage: "Private reply niyetini seçer.",
        placeholders: [
          { token: "{{data}}", meaning: "Post ve parent comment bağlamı" }
        ]
      }
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:239",
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:266",
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:242",
      "api-v2/app/src/modules/QpienAi/services/intentCapabilities.service.ts:24"
    ],
    channels: [
      "all",
      "skills",
      "commerce",
      "chat",
      "comment",
      "question",
      "privateReply"
    ],
    position: { x: 3600, y: 120 }
  },
  {
    id: "intent-decision",
    title: "Intent sonucu route seçer",
    shortTitle: "Intent result",
    subtitle: "Tek cevap, RAG, aksiyon veya paralel akış kararı.",
    kind: "decision",
    stage: "Routing",
    productSummary:
      "Intent promptu teknik cevabı değil, ürün seviyesinde hangi cevap stratejisinin kullanılacağını seçer.",
    details: [
      "Spam doğrudan spam handling'e gider.",
      "İnsan desteği veya basit cevap doğrudan çıkabilir.",
      "RAG ve action dalları gerektiğinde ayrı ayrı veya paralel çalışır.",
      "`commerce` sonucu legacy Action runner'a değil stateful CommerceWorkflowOrchestrator'a gider.",
      "İlk commerce mesajında RAG ve commerce workflow'u aynı anda (paralel) çalışır; commerce cevabı normal bir keşif cevabıysa (STANDARD_FINALIZE) iki cevap tek mesajda birleştirilir, aksi halde yalnız commerce cevabı kullanılır."
    ],
    inputs: ["Intent action", "Intent answer"],
    outputs: [
      "Direct answer",
      "RAG route",
      "Action route",
      "Commerce workflow route",
      "Spam/humanSupport"
    ],
    decisionOutcomes: [
      "spam -> spam conversation",
      "humanSupport -> agent handoff",
      "basic/helped/clarify -> direct answer",
      "rag -> knowledge answer",
      "action -> tool/action answer",
      "ragAndAction -> parallel answer candidates",
      "commerce (ilk mesaj) -> stateful MCP workflow + paralel RAG sorgusu"
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:297"
    ],
    channels: [
      "all",
      "commerce",
      "chat",
      "comment",
      "question",
      "privateReply"
    ],
    position: { x: 3960, y: 120 }
  },
  {
    id: "intent-direct",
    title: "Intent: direkt cevap",
    shortTitle: "basic / helped / clarify",
    subtitle: "Chat prompt cevabı başka kaynak aramadan kullanılabilir.",
    kind: "decision",
    stage: "Intent output",
    productSummary:
      "Chat intent promptu kullanıcının isteğini yeterince basit bulursa RAG veya aksiyon çalıştırmadan cevap üretir ya da açıklama ister.",
    details: [
      "`basic`, `helped`, `humanSupport` ve `clarify` direkt dönüşleri chat intent'inde desteklenir.",
      "Comment/question/private reply tarafında `basic` ve `clarify` direkt dönüş değildir; bu kanallarda direkt dönüş sadece `helped` veya `humanSupport` olabilir.",
      "`helped` pozitif AI cevabı olarak işaretlenir.",
      "`clarify` kullanıcıdan daha net bilgi istemek için kullanılır."
    ],
    inputs: ["Intent action", "Intent answer", "Detected language"],
    outputs: [
      "Prepared answer",
      "positive action when helped",
      "clarify answer"
    ],
    decisionOutcomes: [
      "basic -> direkt chat cevabı",
      "helped -> pozitif cevap",
      "clarify -> sadece chat netleştirme sorusu",
      "comment/question/private reply -> basic/clarify yok"
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:315"
    ],
    channels: ["all", "chat"],
    position: { x: 4320, y: 120 }
  },
  {
    id: "intent-human",
    title: "Intent: insan desteği",
    shortTitle: "humanSupport",
    subtitle: "AI cevaplamamalı, temsilciye bırakmalı.",
    kind: "decision",
    stage: "Intent output",
    productSummary:
      "Prompt kullanıcının isteğinin AI tarafından güvenli veya yeterli şekilde cevaplanamayacağını anlarsa insan desteğine aktarır.",
    details: [
      "`humanSupport` chat tarafında destek aksiyonunu tetikler.",
      "Comment/question/private reply akışında AI cevaplamama reason'ı saklanabilir.",
      "Final prompt bazı comment/question humanSupport sonuçlarında çalışmaz.",
      "`chatSettings.humanHandoff.isDisabled` açık ve kanal o listede ise aktarma hiç yapılmaz: eskalasyon, müşterinin yazdığı mesajla normal bir cevaba dönüşür ve finalize'dan geçer. Bu ayar yalnız chat'tedir: ticket olmayan e-posta (`emailSettings`), ticket, yorum ve marketplace sorusu her zaman insana aktarılabilir."
    ],
    inputs: [
      "Intent action",
      "Reason",
      "Message type",
      "chatSettings.humanHandoff"
    ],
    outputs: ["Human support action", "Not-response reason when applicable"],
    decisionOutcomes: [
      "Chat/email -> human support button action",
      "Comment/question -> AI not response reason",
      "Chat/e-posta bloğunda kanal handoff kapalı -> aktarma yok, ayarlardaki mesaj cevap olarak gider"
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:315",
      "api-v2/app/src/modules/QpienAi/services/qpienAi.service.ts:850",
      "api-v2/app/src/modules/QpienAi/core/humanHandoff.utils.ts:1",
      "api-v2/app/src/modules/Message/services/platform.service.ts:1601"
    ],
    channels: ["all", "chat", "comment", "question", "privateReply"],
    position: { x: 4320, y: 380 }
  },
  {
    id: "intent-spam",
    title: "Intent: spam",
    shortTitle: "spam",
    subtitle: "Mesaj konuşmayı kirletiyor veya cevaplanmamalı.",
    kind: "decision",
    stage: "Intent output",
    productSummary:
      "Spam guidance ve prompt kararıyla mesaj spam kabul edilirse cevap üretimi durur ve konuşma spam sürecine alınır.",
    details: [
      "`spam` sonucu boş içerikle döner.",
      "Final prompt çalışmaz.",
      "`handleSpamConversation` conversation üzerinde spam aksiyonunu uygular."
    ],
    inputs: ["Intent action", "Spam guidance", "Message content"],
    outputs: ["Spam handling", "No customer answer"],
    decisionOutcomes: ["spam -> spam conversation"],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:315",
      "api-v2/app/src/modules/Message/services/platform.service.ts:1619"
    ],
    channels: ["all", "chat", "comment", "question", "privateReply"],
    position: { x: 4320, y: 620 }
  },
  {
    id: "intent-rag",
    title: "Intent: bilgi tabanı",
    shortTitle: "rag",
    subtitle: "Cevap knowledge base üzerinden aranmalı.",
    kind: "decision",
    stage: "Intent output",
    productSummary:
      "Prompt, cevabın marka/ürün bilgi tabanından güvenli şekilde üretilmesi gerektiğini söyler.",
    details: [
      "`rag` sonucu mesaj tipine göre doğru RAG promptuna yönlenir.",
      "Chat/email `ANSWER_BY_RAG_CHAT`, comment/question/private reply kendi RAG promptlarını kullanır.",
      "Ürün özelinde scope varsa item-specific ve general knowledge birlikte değerlendirilir."
    ],
    inputs: ["Intent answer", "Message type", "Product/channel context"],
    outputs: ["RAG route type", "Knowledge search query"],
    decisionOutcomes: [
      "TEXT/EMAIL -> ANSWER_BY_RAG_CHAT",
      "COMMENT -> ANSWER_BY_RAG_COMMENT",
      "QUESTION -> ANSWER_BY_RAG_QUESTION",
      "PRIVATE_REPLY -> ANSWER_BY_RAG_PRIVATE_REPLY"
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:398",
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:712"
    ],
    channels: [
      "all",
      "commerce",
      "chat",
      "comment",
      "question",
      "privateReply"
    ],
    position: { x: 4320, y: -380 }
  },
  {
    id: "intent-action",
    title: "Intent: aksiyon",
    shortTitle: "action",
    subtitle: "Bir tool veya dış sistem aksiyonu gerekebilir.",
    kind: "decision",
    stage: "Intent output",
    productSummary:
      "Prompt, kullanıcının isteğinin bilgi tabanından değil, kanal aksiyonlarından biriyle çözülebileceğini söyler.",
    details: [
      "`action` sonucu doğrudan `ACTION_DETECTION` adımına geçer.",
      "Intent answer, action detection için normalize edilmiş sorgu olur.",
      "Görsel context ve kişi email/telefon bilgileri action source'a taşınır."
    ],
    inputs: ["Intent answer", "Available actions", "Person context"],
    outputs: ["ACTION_DETECTION route", "Action source"],
    decisionOutcomes: ["action -> ACTION_DETECTION"],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:358"
    ],
    channels: ["all", "chat", "comment", "question", "privateReply"],
    position: { x: 4320, y: -140 }
  },
  {
    id: "intent-rag-action",
    title: "Intent: RAG + aksiyon",
    shortTitle: "ragAndAction",
    subtitle: "Bilgi tabanı ve aksiyon birlikte çalışmalı.",
    kind: "decision",
    stage: "Intent output",
    productSummary:
      "Kullanıcı isteği hem bilgi tabanı cevabı hem de dış sistem aksiyonu gerektiriyorsa iki dal paralel çalışır.",
    details: [
      "`ragAndAction` sonucu RAG ve action pipeline'larını aynı anda başlatır.",
      "İki cevap adayı source içinde birlikte tutulur.",
      "Final adımında action cevabı tercihli kaynak olarak değerlendirilir."
    ],
    inputs: ["Intent answer", "RAG context", "Action tools"],
    outputs: ["Parallel RAG route", "Parallel action route", "Merged source"],
    decisionOutcomes: [
      "rag branch -> knowledge answer",
      "action branch -> tool answer",
      "merge -> final answer candidate"
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:412",
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:474"
    ],
    channels: ["all", "chat", "comment", "question", "privateReply"],
    position: { x: 4320, y: -620 }
  },
  {
    id: "rag-retrieval",
    title: "Bilgi tabanı aranır",
    shortTitle: "RAG retrieval",
    subtitle: "Genel veya ürün özelinde ilgili knowledge chunk'ları bulunur.",
    kind: "rag",
    stage: "RAG",
    productSummary:
      "AI cevabı marka bilgisinden üretilecekse önce ilgili bilgi parçaları bulunur.",
    details: [
      "Contextual RAG açıksa V2 kullanılır; hata olursa legacy RAG'a düşer.",
      "Marketplace item-specific scope varsa ürün özelinde ve genel knowledge birlikte aranır.",
      "Human agent cevaplarından öğrenme kapalıysa bu kaynak tipi hariç tutulur.",
      "Bilgi kaynağı kanallara kısıtlanabilir (`source.channels`; boş = tüm kanallar). Kısıt, arama turun `channelId`'sini taşıdığında uygulanır: başka kanallara kısıtlı kaynaklar Pinecone'da `aiKnowledgeSourceId` `$nin`, Elastic'te `must_not` ve sonuçta post-filtre ile dışlanır. `channelId`'yi yalnız v2 geçirir; v1 aramasında bu kısıt uygulanmaz."
    ],
    inputs: ["Query", "Customer id", "Knowledge scope", "AI profile filters"],
    outputs: ["Relevant chunks", "Retrieval source"],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/qpienAi.service.ts:191",
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:1227"
    ],
    channels: [
      "all",
      "commerce",
      "chat",
      "comment",
      "question",
      "privateReply"
    ],
    position: { x: 4680, y: -380 }
  },
  {
    id: "rag-answer",
    title: "RAG cevabı üretilir",
    shortTitle: "RAG answer",
    subtitle: "Bulunan bilgi parçalarından cevap çıkarılır.",
    kind: "prompt",
    stage: "RAG",
    productSummary:
      "AI, bilgi tabanı parçalarını ve ürün/kanaI bağlamını kullanarak cevap üretir; yetersiz bilgi varsa insan desteğine dönebilir.",
    details: [
      "Chat RAG `{{context}}` ile çalışır.",
      "Comment/question/private reply RAG `{{data}}` ve `{{product_details}}` alanlarını doldurur.",
      "Negatif/yetersiz sonuçlarda suggestion üretimi tetiklenebilir."
    ],
    inputs: [
      "Relevant chunks",
      "Enriched query",
      "Product/channel details",
      "Guidances"
    ],
    outputs: ["Answer", "needsSuggestion", "positive/humanSupport"],
    promptNames: [
      {
        name: "ANSWER_BY_RAG_CHAT",
        usage: "Chat/email için bilgi tabanı cevabı.",
        placeholders: [
          { token: "{{context}}", meaning: "Bulunan RAG chunk listesi" },
          { token: "{{detected_language}}", meaning: "Kullanıcı dili" },
          { token: "{{others_guidance}}", meaning: "Diğer guidance notları" }
        ]
      },
      {
        name: "ANSWER_BY_RAG_COMMENT",
        usage: "Yorum için bilgi tabanı cevabı.",
        placeholders: [
          { token: "{{data}}", meaning: "RAG chunk listesi" },
          { token: "{{product_details}}", meaning: "Post/ürün bağlamı" },
          { token: "{{others_guidance}}", meaning: "Diğer guidance notları" }
        ]
      },
      {
        name: "ANSWER_BY_RAG_QUESTION",
        usage: "Marketplace question için bilgi tabanı cevabı."
      },
      {
        name: "ANSWER_BY_RAG_PRIVATE_REPLY",
        usage: "Private reply için bilgi tabanı cevabı."
      },
      {
        name: "GENERATE_SUGGESTION",
        usage: "AI cevaplayamadığında iyileştirme/suggestion üretir."
      }
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:1088",
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:1171"
    ],
    channels: [
      "all",
      "commerce",
      "chat",
      "comment",
      "question",
      "privateReply"
    ],
    position: { x: 5040, y: -380 }
  },
  {
    id: "action-detection",
    title: "Aksiyon/tool seçimi yapılır",
    shortTitle: "ACTION_DETECTION",
    subtitle: "Kanal aksiyonları LLM'e function tool olarak verilir.",
    kind: "prompt",
    stage: "Action",
    productSummary:
      "Sipariş sorgulama, kargo takip veya dış sistem isteği gibi operasyonel işler için doğru aksiyon seçilir.",
    details: [
      "Kanal aksiyonları action adı, açıklama ve collectInfo alanlarıyla tool listesine çevrilir.",
      "Eksik bilgi varsa `request_missing_info` tool'u kullanıcıdan bilgi ister.",
      "Hiç tool eşleşmezse RAG fallback çalışır."
    ],
    inputs: [
      "Customer/channel actions",
      "Query",
      "Person email/phone",
      "Pending source"
    ],
    outputs: ["Tool call", "Missing info request", "No tool fallback"],
    decisionOutcomes: [
      "no_tool_match -> RAG fallback",
      "request_missing_info -> waitingForAnswer=true",
      "matched tool -> HTTP/action request"
    ],
    promptNames: [
      {
        name: "ACTION_DETECTION",
        usage: "Kullanıcının isteğine uygun aksiyon tool'unu seçer."
      }
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:761",
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:832"
    ],
    channels: ["all", "chat", "comment", "question", "privateReply"],
    position: { x: 4680, y: -140 }
  },
  {
    id: "action-request",
    title: "Aksiyon isteği çalışır",
    shortTitle: "Action request",
    subtitle: "Seçilen action gerçek servis isteğine dönüşür.",
    kind: "action",
    stage: "Action",
    productSummary:
      "AI'nin seçtiği operasyonel aksiyon çalıştırılır ve sonucu cevap promptuna kaynak olarak verilir.",
    details: [
      "Tool argümanları action collectInfo alanlarıyla eşleşir.",
      "Action service gerekli HTTP/request işlemini yapar.",
      "Dönen sonuç source olarak action answer promptuna eklenir."
    ],
    inputs: ["Action", "Tool args", "Product id", "AI reply settings"],
    outputs: ["Action result source", "Action answer route"],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:886",
      "api-v2/app/src/modules/QpienAi/services/subServices/aiAction.service.ts:1"
    ],
    channels: ["all", "chat", "comment", "question", "privateReply"],
    position: { x: 5040, y: -140 }
  },
  {
    id: "action-answer",
    title: "Aksiyon sonucu cevaplanır",
    shortTitle: "Action answer",
    subtitle: "Tool sonucu kullanıcı dilinde cevaba çevrilir.",
    kind: "prompt",
    stage: "Action",
    productSummary:
      "Dış sistemden gelen teknik aksiyon sonucu müşteriye anlaşılır bir cevap olarak yazılır.",
    details: [
      "Tool sonucu previousMessages içine tool response olarak eklenir.",
      "Action instruction ve guidance prompta yerleştirilir.",
      "Chat/comment/private reply için uygun action answer promptu seçilir."
    ],
    inputs: [
      "Action result",
      "Action instructions",
      "Guidances",
      "Previous messages"
    ],
    outputs: ["Action-based answer", "Source"],
    promptNames: [
      {
        name: "ANSWER_BY_ACTION_CHAT",
        usage: "Chat/email için action sonucunu cevaba çevirir.",
        placeholders: [
          {
            token: "{ActionInstructions}",
            meaning: "Action'a özel cevaplama yönergesi"
          },
          { token: "{{others_guidance}}", meaning: "Diğer guidance notları" }
        ]
      },
      {
        name: "ANSWER_BY_ACTION_COMMENT",
        usage: "Comment/question için action sonucunu cevaba çevirir."
      },
      {
        name: "ANSWER_BY_ACTION_PRIVATE_REPLY",
        usage: "Private reply için action sonucunu cevaba çevirir."
      }
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:982"
    ],
    channels: ["all", "chat", "comment", "question", "privateReply"],
    position: { x: 5400, y: -140 }
  },
  {
    id: "rag-action-merge",
    title: "RAG + action sonucu birleştirilir",
    shortTitle: "ragAndAction",
    subtitle: "İki kaynak paralel çalışınca final adayları tek pakete alınır.",
    kind: "decision",
    stage: "Merge",
    productSummary:
      "Hem bilgi tabanı hem aksiyon gerekiyorsa iki cevap adayı saklanır; action sonucu tercihli source olarak işaretlenir.",
    details: [
      "RAG ve action pipeline paralel çalışır.",
      "Yalnız GERÇEKTEN cevap veren dal metin katar: boş içerik veya `humanSupport` dönen dal `{ unavailable: true }` olarak işaretlenir, özür metni ne birleştirilir ne finalize'a aday olarak gider.",
      "Bu koruma olmadan bir dalın 'yardımcı olamıyorum' metni birleşik cevabın başına geçip finalize'ın tüm cevabı reddetmesine yol açıyordu.",
      "Source içinde `ragAndAction` alanı final prompt için adayları taşır; action sonucu tercihli kaynak olarak işaretlenir."
    ],
    inputs: ["RAG answer", "Action answer"],
    outputs: [
      "Combined content",
      "answerCandidates (unavailable dahil)",
      "Conflict/source context"
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:412",
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:1272"
    ],
    channels: ["all", "chat", "comment", "question", "privateReply"],
    position: { x: 5760, y: -260 }
  },
  {
    id: "direct-outcome",
    title: "Direkt cevap finalize edilir",
    shortTitle: "Direct answer",
    subtitle: "Basit veya netleştirme cevabı final tona gider.",
    kind: "output",
    stage: "Output",
    productSummary:
      "Intent promptunun direkt cevap verdiği durumlarda cevap yine marka tonu ve uzunluk ayarlarından geçirilir.",
    details: [
      "Basic/helped/clarify cevapları burada finalize adımına hazırlanır.",
      "Bu path pratikte chat direct cevaplarını temsil eder; comment/question/private reply direct helped cevabı da sonuç olarak finalize katmanına gelebilir ama `clarify/basic` bu kanallarda yoktur.",
      "Bu dal RAG veya aksiyon kaynağı taşımaz.",
      "Final adımı tone of voice ve answer length kurallarını uygular."
    ],
    inputs: ["Intent answer", "Intent action", "Detected language"],
    outputs: ["Prepared direct answer"],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:315",
      "api-v2/app/src/modules/QpienAi/services/qpienAi.service.ts:863"
    ],
    channels: ["all", "chat"],
    position: { x: 4680, y: 120 }
  },
  {
    id: "private-reply",
    title: "Yorum + otomatik DM tek turda koordine edilir",
    shortTitle: "Comment + auto DM",
    subtitle: "Aynı müşteri mesajı, iki gönderim yolu, tek tur.",
    kind: "platform",
    stage: "Platform",
    productSummary:
      "Comment ayarında özel mesaj açıksa aynı yorum hem halka açık cevaplanır hem DM ile yanıtlanır. İki taraf ayrı ayrı çalışan iki AI turu DEĞİLDİR: pahalı ve tekil olan her şey bir kez yapılır, yalnız kanal bazlı olan kısımlar iki kez.",
    details: [
      "İki taraf aynı alt mesajı cevaplar ama ayrı mesaj-tipi guidance seti ve ayrı intent kararı ister; bu yüzden intent aşaması iki kez, paralel çalışır.",
      "Bir kez yapılanlar: turn enrichment (sonuç iki tarafa paylaştırılır — `classifiedAiRole` dahil, aksi halde koordine edilen yol rol sınıflandırmasını kaybediyordu) ve Skills yönlendirmesi.",
      "Skills koordinatörden TAM BİR KEZ çalışır, kardeş çağrılar `skipSkillsRoute: true` taşır. Bu doğruluk şartıdır: Skills oturumu aynı anda tek tur kabul eder, iki kardeş `Promise.all` içinde girdiğinde kaybeden taraf `SKILL_SESSION_BUSY` → `NO_RESPONSE` alıp sessizce cevapsız kalır; ters zamanlamada ise bir müşteri mesajı iki tur harcayıp Skill'i iki adım ilerletir.",
      "Skill sahiplenirse hedefi Skill'in kendisi söyler: iki metin varsa yorum `content`, DM `dmContent` alır; yalnız DM işaretliyse halka açık hiçbir şey gönderilmez; işaret yoksa yalnız yorum cevaplanır. Her yarı, gerçekten gittiği tarafın parametreleriyle finalize edilir.",
      "İki taraf `commerce`'e çıkarsa commerce workflow'u BİR kez çalışır ve guidance'sız temel içerik paylaşılan tarafa devredilir; DM tarafı `skipCommerceResume: true` taşır.",
      "Intent aşamasından sonrası, o aşamanın çözdüğü role göre yürür (commerce workflow kimliği AI profilini içerir, RAG/şekillendirme prompt satırları rol metnidir). İki taraf ayrı ayrı çözülür.",
      "İki finalize çağrısı sonda paralel çalışır. DM sonucu spam veya humanSupport ise public comment aksiyonunu etkileyebilir.",
      "Bilinen boşluk: yorumda halka açık cevap ve/veya otomatik DM gönderilip gönderilmeyeceği GEÇİCİ profilin `commentSettings` ayarından okunur, çünkü iki çağrı da cevap var olmadan önce kurulur. Uygun roller üzerinden birleşim almak hiçbir rolün istemediği bir DM EKLEYECEĞİ için bu bilinçli olarak ürün kararına bırakıldı."
    ],
    inputs: [
      "Comment params + private reply params",
      "Shared enrichment",
      "Skill route",
      "Post/comment/product context"
    ],
    outputs: [
      "Comment answer",
      "Private reply message",
      "Merged spam/humanSupport state"
    ],
    promptNames: [
      {
        name: "INTENT_DETECTION_PRIVATE_REPLY",
        usage:
          "DM tarafının kendi intent kararını verir (yorum tarafıyla paralel)."
      }
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/qpienAi.service.ts:1867",
      "api-v2/app/src/modules/QpienAi/services/qpienAi.service.ts:1885",
      "api-v2/app/src/modules/Message/services/platform.service.ts:1488"
    ],
    channels: ["all", "comment", "privateReply"],
    position: { x: 6840, y: 400 }
  },
  {
    id: "finalize",
    title: "Final cevap düzenlenir",
    shortTitle: "FINALIZE_AI_CONTENT",
    subtitle: "Ton, uzunluk, iletişim stili ve tanıtım kuralı uygulanır.",
    kind: "prompt",
    stage: "Final",
    productSummary:
      "Ana cevap üretildikten sonra marka tonu ve AI profil ayarlarına uygun son kullanıcı cevabına dönüştürülür.",
    details: [
      "`handleAskQuestion`, spam ve içeriksiz humanSupport sonuçları hariç final promptu çağırır. RAG, action, commerce ve Skill cevapları aynı adımdan geçer — hiçbir kaynak kendi tonuyla gönderilmez.",
      "Ton/uzunluk/emoji/persona ayarları CEVAP VEREN rolden okunur (`answer.resolvedAiRole` → `pickAnsweringProfile`), turun başındaki geçici rolden değil.",
      "Commerce ve Skill cevabında yalnız `STANDARD_FINALIZE` bu adıma gelir; `PRESERVE_EXACT` finansal/approval/transaction metnini değişmeden geçirir.",
      "Tone of voice, answer length, communication guidance, emoji kuralı ve self-introduction rule yerleştirilir.",
      "Comment / marketplace question / private reply tek-atımlık kanallardır: prompta 'Single-Reply Channel Rule' eklenir ve bu kural promptun geri kalanının ÜSTÜNDEDİR — cevap soruyla veya 'başka bir şey?' ifadesiyle bitemez.",
      "Paralel dallarda (ragAndAction, RAG + commerce) cevap VERMEYEN dal metin katmaz: boş içerik veya `humanSupport` dönen dal `answerCandidates.<dal>Answer = { unavailable: true }` olarak bildirilir, özür metni birleştirilmez.",
      "Commerce ürün kartları varsa başlık + fiyat olarak `renderedProductCards` ile kanıt niyetine geçirilir (en fazla 3 kart) — kartları Message modülü çizer, metnin kartları tekrar listelemesi beklenmez, link yazılmaz.",
      "Sonuç `APPROVED` / `REVISED` / `REJECTED` olur; `REJECTED` comment/question tarafında boş içerik üretir ve `aiNotResponseReason` yazılır."
    ],
    inputs: [
      "Prepared answer",
      "Raw user query",
      "Answering AI profile (resolvedAiRole)",
      "Guidances",
      "Source / answerCandidates",
      "renderedProductCards (commerce)"
    ],
    outputs: [
      "Final answer",
      "FinalizeResponse.action (APPROVED / REVISED / REJECTED)",
      "didAiIntroduceItself flag"
    ],
    promptNames: [
      {
        name: "FINALIZE_AI_CONTENT",
        usage:
          "Cevabı marka tonuna ve AI profil ayarlarına göre son haline getirir.",
        placeholders: [
          {
            token: "{{tone_of_voice}}",
            meaning: "Formal / neutral / friendly"
          },
          {
            token: "{{answer_length}}",
            meaning: "Concise / standard / thorough"
          },
          {
            token: "{{communication_style_guidance}}",
            meaning: "İletişim stili guidance notları"
          },
          { token: "{{introduce_rule}}", meaning: "AI kendini tanıtsın kuralı" }
        ]
      }
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:1723",
      "api-v2/app/src/modules/QpienAi/services/qpienAi.service.ts:4004",
      "api-v2/app/src/modules/QpienAi/core/aiRole.utils.ts"
    ],
    channels: [
      "all",
      "skills",
      "commerce",
      "chat",
      "comment",
      "question",
      "privateReply"
    ],
    position: { x: 6120, y: 120 }
  },
  {
    id: "send-and-memory",
    title: "Cevap gönderilir ve hafıza güncellenir",
    shortTitle: "Send + memory",
    subtitle:
      "Kanal aksiyonu, mesaj kaydı, memory ve kota işlemleri tamamlanır.",
    kind: "output",
    stage: "Output",
    productSummary:
      "Final cevap kanala göre gönderilir veya insan desteği/spam aksiyonu uygulanır; AI memory gelecekteki cevaplar için güncellenir.",
    details: [
      "`aiAction` bir başarı işareti değil GÖNDERİM YOLU seçicisidir: comment/question/private reply tarafında `positive` yazılır ve cevap inbound `msgId` ile `processAiButtonAction` üzerinden gider; chat tarafında `aiAction` bilinçli olarak boş bırakılır ve `handleReplyByAi` mesajı kendisi oluşturur.",
      "AI cevabı memory'ye eklenir; waitingForAnswer ve action id state'i güncellenir.",
      "Tespit edilen dil `AiPersonMemory.lastDetectedLanguage` alanına yazılır; sonraki turlar, otomatik kapanış ve özet/etiket promptları bunu kullanır.",
      "Görsel context persist edilir.",
      "Ücretlendirme `controlAndPay` ile tam bir kez yapılır: `REJECTED` finalize hiç ücretlendirilmez, `aiAnswers` kotası varsa bir birim düşülür, yoksa hesap bakiyesinden 0,05 USD çekilir; bakiye 0,05'in altına inerse AI profili kapatılır ve müşteriye e-posta gider.",
      "Aynı `messageId` için ücretlendirme Redis'te 24 saatlik `NX` kilidiyle tekilleştirilir; RabbitMQ yeniden teslim ettiğinde ikinci kez ücret çıkmaz.",
      "Cevap `humanSupport` ise `aiNotResponseReason` saklanır ve mesaj kartında 'AI neden cevaplamadı' bilgisi gösterilir — artık sadece comment/question değil chat ve e-posta için de.",
      'Commerce cevabında gösterilen ürün kartları (presentedProductCards) sırasıyla hafızaya yazılır; kart gösteremeyen kanallarda numaralı metin ("1) Başlık — fiyat") kullanılır. Sonraki mesajlarda bu kayıt yalnız geçmiş referans olarak okunur, güncel fiyat/stok için tool\'lar yeniden çağrılır.'
    ],
    inputs: [
      "Final AI answer",
      "AI action",
      "Channel settings",
      "Conversation"
    ],
    outputs: [
      "Outgoing message/action",
      "Updated AI memory",
      "lastDetectedLanguage",
      "Usage payment / aiNotResponseReason"
    ],
    codeRefs: [
      "api-v2/app/src/modules/Message/services/platform.service.ts:1573",
      "api-v2/app/src/modules/Message/services/platform.service.ts:1634",
      "api-v2/app/src/modules/QpienAi/services/qpienAi.service.ts:2167",
      "api-v2/app/src/modules/Message/services/aiVisualContext.utils.ts:227"
    ],
    channels: [
      "all",
      "skills",
      "commerce",
      "chat",
      "comment",
      "question",
      "privateReply"
    ],
    position: { x: 6480, y: 120 }
  },
  {
    id: "commerce-workflow-gateway",
    title: "Aktif commerce workflow kontrol edilir",
    shortTitle: "Active workflow gateway",
    subtitle: "Her mesajda önce kalıcı bir commerce süreci var mı bakılır.",
    kind: "workflow",
    stage: "Commerce entry",
    productSummary:
      "Devam eden ürün/sipariş işlemi varsa mesaj yeniden intent'e sokulmaz; workflow kaldığı adımdan deterministik biçimde devam eder.",
    details: [
      "Kontrol `handleAskQuestion` içinde, turn enrichment ve Skills yönlendirmesinden SONRA, genel `askQuestion()` pipeline'ından ÖNCE yapılır.",
      "Workflow; customer, person, conversation, AI profile ve channel kimliğiyle bulunur.",
      "Workflow kimliği AI profilini (rolü) içerdiği için resume denemesi sırayla yapılır: konuşma bir role oturmuşsa yalnız o rol, oturmamışsa (1) enrich'in sınıflandırdığı rol, (2) geçici rol, (3) kalan uygun roller. Bu olmadan başka bir rol altında başlamış bir sepet sessizce kayboluyordu; boşa deneme iki indeksli okumadır, yazma yapmaz.",
      "Aynı inbound `messageId` ikinci kez işlenmez; önceki exact cevap güvenli biçimde replay edilebilir.",
      "Resume sırasında bir hata olursa akış fail-closed davranır: kurtarılabilir hata `FAILED_RECOVERABLE` olarak yazılır, aksi halde 'commerce şu an kullanılamıyor' cevabı döner — hata genel pipeline'a sızmaz.",
      "Aktif workflow yoksa guard → rol çözümü → guidance → intent akışı aynen devam eder.",
      "Commerce workflow, legacy Action `waitingForAnswer` state'ini kullanmaz."
    ],
    inputs: [
      "Message id",
      "Conversation identity",
      "Eligible AI profiles",
      "classifiedAiRole",
      "Channel"
    ],
    outputs: [
      "Resume commerce",
      "Continue global pipeline",
      "Replay/NO_RESPONSE",
      "FAILED_RECOVERABLE"
    ],
    decisionOutcomes: [
      "Aktif workflow -> CommerceWorkflowOrchestrator.tryResume",
      "Workflow yok -> mevcut global pipeline",
      "İşlenmiş messageId -> stored response / NO_RESPONSE",
      "Resume hatası -> FAILED_RECOVERABLE / commerce unavailable"
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/qpienAi.service.ts:1550",
      "api-v2/app/src/modules/QpienAi/services/qpienAi.service.ts:1694",
      "api-v2/app/src/modules/QpienAi/drivers/commerceWorkflow.mongo.driver.ts:112"
    ],
    channels: [
      "all",
      "skills",
      "commerce",
      "chat",
      "comment",
      "question",
      "privateReply"
    ],
    position: { x: 2160, y: -900 }
  },
  {
    id: "commerce-continuation",
    title: "Devam mesajı sınıflandırılır",
    shortTitle: "Continuation classifier",
    subtitle:
      "Kısa cevap, yan soru, konu değişimi ve iptal birbirinden ayrılır.",
    kind: "prompt",
    stage: "Commerce workflow",
    productSummary:
      "“42”, “ikincisi”, “bir tane” gibi eksik görünen mesajlar global intent yerine beklenen commerce adımının bağlamında yorumlanır.",
    details: [
      "Classifier yalnız devam mesajının türünü seçer; state transition veya write kararı vermez.",
      "EXPECTED_INPUT hedefli slot extractor'a gider.",
      "COMMERCE_SIDE_QUESTION read-only agent ile cevaplanır ve beklenen adım yeniden hatırlatılır.",
      "NEW_TOPIC workflow'u SUSPENDED yapar ve mesajı mevcut pipeline'a bırakır.",
      "RESUME askıdaki workflow'u geri açar; CANCEL yalnız workflow'u sonlandırır.",
      "Approval beklerken araya giren yan soru proposal'ı yeniden hazırlar; eski approval geçersiz kalır."
    ],
    inputs: ["Current workflow", "Expected input type", "Inbound query"],
    outputs: [
      "EXPECTED_INPUT",
      "COMMERCE_SIDE_QUESTION",
      "NEW_TOPIC",
      "RESUME",
      "CANCEL"
    ],
    decisionOutcomes: [
      "EXPECTED_INPUT -> slot/state advance",
      "SIDE_QUESTION -> read-only answer + reminder",
      "NEW_TOPIC -> suspend + global pipeline",
      "RESUME -> workflow resume",
      "CANCEL -> workflow cancelled"
    ],
    promptNames: [
      {
        name: "COMMERCE_CONTINUATION_CLASSIFIER",
        usage:
          "Aktif workflow içindeki mesajı sınıflandırır; transition kararı kodda uygulanır."
      }
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/commerceWorkflowOrchestrator.service.ts:120",
      "api-v2/app/src/modules/QpienAi/services/commerceWorkflowOrchestrator.service.ts:287"
    ],
    channels: ["all", "commerce"],
    position: { x: 2520, y: -900 }
  },
  {
    id: "commerce-orchestrator",
    title: "Stateful commerce workflow yönetilir",
    shortTitle: "Commerce orchestrator",
    subtitle:
      "İlk commerce intent'i ve devam mesajları aynı state machine'de birleşir.",
    kind: "workflow",
    stage: "Commerce workflow",
    productSummary:
      "Ürün keşfi, sipariş oluşturma ve sipariş iptali için kalıcı, sürümlü ve restart sonrası devam edebilen bir süreç yürütülür.",
    details: [
      "İlk mesaj intent=`commerce` olduğunda `start`; aktif süreçte sonraki mesajlarda `tryResume` çalışır.",
      "Workflow tipleri PRODUCT_DISCOVERY, ORDER_CREATE ve ORDER_CANCEL'dır.",
      "Durumlar ACTIVE, WAITING_INPUT, WAITING_APPROVAL, EXECUTING, RECONCILING, SUSPENDED ve terminal/failure durumlarını kapsar.",
      "Mongo transition'ları version tabanlı optimistic CAS ile yapılır.",
      "Conversation başına tek aktif write workflow korunur; processing lease performans içindir, doğruluk kaynağı persistent state'tir.",
      "Eksik/zayıf commerce prompt'unda readiness kontrolü fail-closed çalışır.",
      "Rerank bazı/tüm adayları reddederse (`rerankRejectedAllCandidates` / `rerankConfirmedIds`), yalnız gerçekten onaylanan ürünler kart olarak gösterilir; ilgisiz arama sonuçları sessizce elenir.",
      "Sonuçlar arasında tek baskın bir eşleşme varsa gereksiz yere 'hangisini istersiniz' seçim sorusu sorulmaz; 2+ gerçek seçenek varsa kullanıcıdan seçim istenir (bu karar LLM değil koddur).",
      "Kategoriye özel olmayan genel PM kuralları ('OTHERS guidance') önce arama promptuna eklenir; yeterince güvenilir olmadığı durumlar için ayrı bir `COMMERCE_GUIDANCE_ENFORCEMENT` adımı hazır cevabı bu kurallara göre best-effort yeniden şekillendirir."
    ],
    inputs: [
      "First commerce intent or resume classification",
      "Workflow identity",
      "Current version"
    ],
    outputs: [
      "Deterministic next step",
      "Persistent workflow state",
      "Recoverable failure"
    ],
    decisionOutcomes: [
      "PRODUCT_DISCOVERY -> bounded read agent",
      "ORDER_CREATE -> slot collection + proposal + approval",
      "ORDER_CANCEL -> owned order + cancellation proposal + approval",
      "Failure -> FAILED_RECOVERABLE"
    ],
    promptNames: [
      {
        name: "COMMERCE_GUIDANCE_ENFORCEMENT",
        usage:
          "OTHERS guidance kurallarının cevaba mutlaka yansıması için hazır commerce cevabını best-effort yeniden şekillendirir; uygulanacak kural yoksa adım sessizce atlanır."
      }
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/commerceWorkflowOrchestrator.service.ts:72",
      "api-v2/app/src/modules/QpienAi/drivers/commerceWorkflow.mongo.driver.ts:132",
      "api-v2/app/src/modules/QpienAi/services/commerceWorkflowOrchestrator.service.ts:146",
      "api-v2/app/src/modules/QpienAi/services/commerceWorkflowOrchestrator.service.ts:240",
      "api-v2/app/src/modules/QpienAi/services/commerceWorkflowOrchestrator.service.ts:2420",
      "api-v2/app/src/modules/QpienAi/services/commerceWorkflowOrchestrator.service.ts:2449"
    ],
    channels: ["all", "commerce"],
    position: { x: 4320, y: -900 }
  },
  {
    id: "commerce-slot-state",
    title: "Slot ve workflow adımı ilerletilir",
    shortTitle: "Slot + state transition",
    subtitle: "Ürün, varyant, adet, adres, kargo ve ödeme adım adım toplanır.",
    kind: "workflow",
    stage: "Commerce workflow",
    productSummary:
      "LLM yalnız yapılandırılmış aday bilgiyi çıkarır; hangi adımın geçerli olduğu ve state'in nasıl değişeceği kod tarafından belirlenir.",
    details: [
      "Adımlar discovery, order, product, variant, quantity, address, shipping, payment, prepare, approval, place ve result'tır.",
      "Slot extractor yalnız ilgili adımın izinli alanlarını çıkarır.",
      "Ürün/varyant/order adayları MCP'den gelen opaque ID'lerle state'e bağlanır.",
      "Quantity pozitif integer olarak doğrulanır; address modele tam metin olarak verilmez, yalnız `addressRef` tutulur.",
      "“Evet ama iki tane olsun” approval değildir: quantity değişir, proposal ve challenge yeniden üretilir."
    ],
    inputs: [
      "Workflow step",
      "Expected input",
      "Candidate IDs",
      "Inbound message"
    ],
    outputs: [
      "Validated slots",
      "WAITING_INPUT",
      "Ready to prepare",
      "Updated version"
    ],
    promptNames: [
      {
        name: "COMMERCE_SLOT_EXTRACTOR",
        usage:
          "Beklenen commerce adımına ait yapılandırılmış alanları çıkarır; state'i kendisi değiştirmez."
      }
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/commerceWorkflowOrchestrator.service.ts:508",
      "api-v2/app/src/modules/QpienAi/core/commerce.types.ts:66"
    ],
    channels: ["all", "commerce"],
    position: { x: 4680, y: -900 }
  },
  {
    id: "commerce-agent",
    title: "Bounded Commerce Agent çalışır",
    shortTitle: "Bounded commerce agent",
    subtitle: "Sadece mevcut workflow adımının read tool'larını kullanabilir.",
    kind: "workflow",
    stage: "Commerce agent",
    productSummary:
      "Sektöre özel onlarca tool yerine katalog metadata'sı ve generic typed facet'lerle ürün arar; modelin yetkisi kodla sınırlandırılır.",
    details: [
      "En fazla 4 agent round, 6 read tool call ve toplam 64 KB tool çıktısı kabul edilir.",
      "Tool allowlist workflow adımına göre `STEP_TOOLS` sabitinden seçilir.",
      "`place_order` ve `cancel_my_order` modele hiçbir zaman verilmez.",
      "Ürün title/description/option alanları UNTRUSTED_COMMERCE_DATA olarak işaretlenir; prompt talimatı sayılamaz.",
      "Arama sonucu yalnız mevcut aday product ID'leri arasında COMMERCE_PRODUCT_RERANK ile sıralanabilir.",
      "Rerank'a gönderilen adaylar en fazla 10 ürünle ve kısaltılmış alanlarla (title/kategori/marka/relevance) sınırlanır; böylece büyük sonuç kümelerinde JSON ortadan kesilmez.",
      "Rerank zaman aşımı 30 saniyedir; büyük aday listelerinde gereksiz timeout hatası azaltıldı.",
      "Modelin ürettiği hiçbir ID doğrudan MCP'ye gitmez: `productId`/`variantId`/`orderId` argümanları önce güvenilir küme ile karşılaştırılır (host'un verdiği seçimler/adaylar + bu turda tool cevabından gerçekten görülmüş id'ler). Uydurulmuş bir id MCP'ye ulaşmadan düzeltici bir tool mesajıyla geri çevrilir.",
      "'Benzer ürün öner' bir prompt cümlesi değil host kuralıdır: mağaza ayarı kapalıysa rerank yalnız 'istenen ürünün kendisi' saydığı id'leri döndürür ve kart/aday/seçim sorusu yalnız onlarla kurulur — ayar kapalıyken müşteriye 3 muadil kart gösterilmesi böylece bitti.",
      "Arama sonucu geldiği halde katalog 'bu satırlar soruyu karşılamıyor' derse (düşük güven işareti) aday kümesi boşaltılır: kart yok, seçim sorusu yok, agent'ın kendi metni süslenmeden müşteriye gider.",
      "Arama sonucu kullanıcının diliyle uyuşmuyorsa agent otomatik olarak bir kez çevrilmiş sorguyla yeniden arar.",
      "Zaten seçilmiş bir ürün varsa `get_product_details` turdan önce host tarafından çağrılır ve sonuç modele hazır kanıt olarak verilir; bu kanıt yeni bir teklif sayılmaz, kart üretmez. Sebebi: ürün açıklaması ve satıcı notları yalnız bu tool'da var — model kendi seçimine bırakıldığında mağaza politikası tool'una bakıp 'belirtilmiyor' diyordu.",
      "Bilgi eksikse SORMAK da bir eylemdir: her adımda `request_clarification` tool'u tanımlı, MCP'ye gitmez ve turu müşterinin dilinde tek bir soruyla bitirir. Bu olmadan model zorunlu tool çağrısını uydurma bir id ya da cümlenin kendisiyle arama yaparak dolduruyordu.",
      "Soru sorma yeteneği tek-atımlık kanallarda (comment/marketplace question/private reply) KAPALIDIR; orada finalize zaten soruyla bitmeyi yasakladığı için agent'a 'tam ve kendi kendine yeten cevap ver' kuralı verilir.",
      "MCP'den gelen düzeltilebilir hatalar (çok büyük çıktı, şema ihlali) modele düzeltici tool mesajı olarak döner ve daha dar argümanla tekrar denenebilir — en fazla 2 kez. Bağlantı/yetki hataları hemen sonlandırır.",
      "Money total hesaplanmaz; agent yalnız authoritative tool değerlerini aktarabilir."
    ],
    inputs: [
      "Query",
      "Workflow step",
      "Trusted opaque references",
      "Allowed MCP tools"
    ],
    outputs: ["Read tool calls", "Candidate product IDs", "Grounded answer"],
    promptNames: [
      {
        name: "COMMERCE_QUERY_PLANNER",
        usage:
          "Sorguyu izinli read tool'larla çözen bounded agent system promptu."
      },
      {
        name: "COMMERCE_PRODUCT_RERANK",
        usage:
          "Yalnız retrieval'ın döndürdüğü product ID'lerini, kısaltılmış bir aday listesi üzerinden yeniden sıralar; hiçbiri eşleşmiyorsa veya bir kısmı eşleşiyorsa bunu da işaretler."
      }
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/commerceAgentRunner.service.ts",
      "api-v2/app/src/modules/QpienAi/core/commerce.constants.ts:46",
      "api-v2/app/src/modules/QpienAi/core/commerce.constants.ts:146",
      "api-v2/app/src/modules/QpienAi/core/commerce.constants.ts:188",
      "api-v2/app/src/modules/QpienAi/core/commerce.constants.ts:204",
      "api-v2/app/src/modules/QpienAi/core/commerceAgent.utils.ts"
    ],
    channels: ["all", "commerce"],
    position: { x: 5040, y: -900 }
  },
  {
    id: "commerce-mcp-client",
    title: "Çağrıyı model değil Qpien yapar",
    shortTitle: "MCP host (API-v2)",
    subtitle: "Model tool ADINI seçer; isteği kimlikle birlikte host gönderir.",
    kind: "security",
    stage: "MCP client",
    productSummary:
      "Model hiçbir zaman mağaza servisine bağlanmaz. Hangi tool'un çağrılabileceğine ve hangi müşteri adına çağrıldığına Qpien API-v2 karar verir; model yalnız bir isim ve argüman önerir.",
    details: [
      "Kim adına: müşteri (tenant), konuşma ve alışveriş yapan kişi bilgisi isteğe host tarafından eklenir. Model bu alanları göremez, dolayısıyla başka bir müşterinin verisini isteyemez.",
      "Ne kadar yetki: read (okuma) ve write (sipariş oluştur/iptal) yetkileri ayrıdır. Agent'a yalnız read verilir; write yetkisi ancak doğrulanmış bir onay sonrası üretilir.",
      "Her istek kısa ömürlüdür (60 saniye) ve tekil bir istek kimliği taşır.",
      "Ne kadar dar: oturumun katalog kapsamı da header'la taşınır — mağaza, ilan, makale ve sorulan varyant. Marketplace kapsam değerleri merchant metni içerebildiği için (boşluk, Türkçe harf) percent-encoded gönderilir; ham non-ASCII bir header baytı tanımsız davranıştır.",
      "İstek ve cevap şeması iki tarafta da katı doğrulanır; beklenmeyen alan geçmez.",
      "Timeout ve bağlantı hataları audit'e yazılır. Write çağrısının sonucu belirsizse körlemesine tekrar denenmez — uzlaştırma adımına gider.",
      "⚠️ Güvenlik notu: bu kimlik bilgileri şu an düz HTTP header'ıyla taşınıyor ve kriptografik olarak İMZALI DEĞİL (önceden RSA imzalı JWT'ydi). Sunucu tarafında Host/Origin izin listesi + 60 saniyelik tazelik kontrolü var; yine de özel ağ dışından erişilebilen bir ortamda bu header'lar taklit edilebilir."
    ],
    inputs: [
      "Allowed tool name",
      "Validated args",
      "Actor context (host tarafından)",
      "Required scopes"
    ],
    outputs: [
      "MCP isteği (imzasız header context + kapsam header'ları)",
      "Şema doğrulanmış sonuç",
      "Maskelenmiş audit kaydı"
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/commerceMcpClient.service.ts:38",
      "api-v2/app/src/modules/QpienAi/core/commerceConfig.ts:54",
      "qpien-shop-v2/app/src/modules/CommerceMcp/services/commerceMcpAuth.service.ts:33"
    ],
    channels: ["all", "commerce"],
    position: { x: 5400, y: -900 }
  },
  {
    id: "commerce-mcp-server",
    title: "Qpien Shop'un MCP sunucusu isteği doğrular",
    shortTitle: "Qpien Shop /mcp",
    subtitle: "Özel ağda duran, yalnız tool sunan dar bir commerce arayüzü.",
    kind: "mcp",
    stage: "Qpien Shop MCP",
    productSummary:
      "`qpien-shop-v2` üzerinde, yalnızca sayılı commerce işlemini sunan bir MCP sunucusu var. Veritabanına serbest erişim, dosya erişimi veya rastgele URL çağırma yeteneği YOKTUR — sunulan tool listesi neyse yapılabilecek iş odur.",
    details: [
      "Yalnız POST kabul eder; diğer methodlar 405 döner ve istek gövdesi boyutu sınırlıdır.",
      "Her istekte müşteri/konuşma/kişi kimliği ve yetki listesi doğrulanır; süresi geçmiş veya eksik bir istek 401 alır.",
      "Ağ sınırı: yalnız izinli host/origin'den gelen istekler kabul edilir (servis özel ağda durur).",
      "Tool listesi yetkiye göre dinamik: read yetkisiyle gelen bir istek sipariş oluşturma/iptal tool'larını LİSTEDE BİLE göremez.",
      "Kapsama göre de dinamik: `get_listing_group_variants` yalnız oturum bir makale kapsamı taşıdığında register edilir. Storefront oturumu o tool'u hiç görmez.",
      "Kapsam tutarlılığı zorunludur: ilan veya makale kapsamı, mağaza kapsamı OLMADAN gelirse istek 401'dir — ilan anahtarları mağaza bazlı platform id'leridir, mağazasız bir anahtar başka mağazanın ilanını eşleyebilir.",
      "Her tool'un girdi ve çıktısı katı şemayla doğrulanır, çıktı boyutu üst sınırla kesilir.",
      "Ürün metinleri `UNTRUSTED_COMMERCE_DATA` etiketiyle döner: modele 'bu veri, talimat değil' denmiş olur.",
      "Aynı müşteriye ait olsa bile başkasına ait sipariş/adres istenirse ayırt edilemeyen bir NOT_FOUND döner."
    ],
    inputs: [
      "POST JSON-RPC isteği",
      "Kimlik + yetki header'ları",
      "İzinli host/origin",
      "Onay ve idempotency metadata'sı (host-only)"
    ],
    outputs: [
      "Yetkiye göre filtrelenmiş tool listesi",
      "Şema doğrulanmış tool sonucu",
      "Maskelenmiş tool audit kaydı"
    ],
    codeRefs: [
      "qpien-shop-v2/app/src/modules/CommerceMcp/controller/commerceMcp.routes.ts:14",
      "qpien-shop-v2/app/src/modules/CommerceMcp/controller/commerceMcp.routes.ts:99",
      "qpien-shop-v2/app/src/modules/CommerceMcp/server/commerceMcpServer.ts:77"
    ],
    channels: ["all", "commerce"],
    position: { x: 5760, y: -900 }
  },
  {
    id: "commerce-read-tools",
    title: "Katalog ve shopper read tool'ları",
    shortTitle: "Read-only commerce tools",
    subtitle: "Her sektöre ayrı tool değil; ortak bir arama sözleşmesi.",
    kind: "mcp",
    stage: "MCP tools",
    productSummary:
      "Moda, elektronik, kozmetik veya endüstriyel ürün için ayrı arama tool'u yazılmaz: mağazanın kendi kategori/marka/özellik sözlüğü okunur ve arama ortak bir 'özellik + karşılaştırma + değer' sözleşmesiyle yapılır.",
    details: [
      "Katalog tool'ları: resolve_catalog_context, search_products, get_product_details, get_product_variants, compare_products, get_product_policies, get_size_guide.",
      "⚠️ Bu liste STOREFRONT turunundur. Marketplace turunda kısaltılmış bir set verilir (arama ve karşılaştırma yoktur) — bkz. 'Marketplace ≠ Commerce'.",
      "`get_listing_group_variants` yalnız marketplace içindir ve sunucu, oturumda bir makale kapsamı YOKSA tool'u hiç register etmez. Aynı makalenin diğer ilanlarını (renklerini) okur; başka bir ürüne ulaşamaz.",
      "`get_product_details` marketplace turunda ayrıca `variantAxes` döndürür — hangi özelliğin ilan içinde, hangisinin kardeş ilanlarda durduğunu katalogdan hesaplayarak söyler (bkz. 'variantAxes routing').",
      "Alışveriş yapan kişiye ait tool'lar: get_my_orders, get_my_order ve adresi maskeli döndüren get_my_addresses.",
      "resolve_catalog_context hiç ürün satırı çekmeden mağazanın kategori/marka/özellik/seçenek/eşanlamlı sözlüğünü çözer; zorunlu bir ön çağrı değildir.",
      "`resolve_product_by_url` ve `resolve_product_by_marketplace_ref` bu listede vardır ama HİÇBİR adımın model tool listesinde yoktur — yalnız host çağırır (bkz. 'Turun konusu').",
      "Gerçekten farklı bir domain işi gerekirse (örn. araç-parça uyumluluğu) ayrı ve dar kapsamlı bir tool eklenir."
    ],
    inputs: [
      "Tenant catalog",
      "Lexical query",
      "Typed facets",
      "Opaque shopper identity"
    ],
    outputs: [
      "Sanitized products",
      "Verified variants/facets",
      "Owned order/address views"
    ],
    codeRefs: [
      "qpien-shop-v2/app/src/modules/CommerceMcp/server/commerceMcpServer.ts:119",
      "qpien-shop-v2/app/src/modules/Products/services/productDiscovery.service.ts"
    ],
    channels: ["all", "commerce", "question"],
    position: { x: 6120, y: -1140 }
  },
  {
    id: "commerce-prepare",
    title: "Sipariş veya iptal proposal'ı hazırlanır",
    shortTitle: "Prepare proposal",
    subtitle: "Canlı stok/fiyat/kural doğrulanır; sipariş henüz oluşmaz.",
    kind: "transaction",
    stage: "MCP prepare",
    productSummary:
      "Gerekli bilgiler tamamlandığında tutarı mağaza servisi hesaplar ve süreli bir TEKLİF saklar. AI ne toplam tutarı ne sipariş sonucunu uydurabilir: müşteriye gösterilen sayılar bu teklifin içinden gelir.",
    details: [
      "`prepare_order` canlı ürün, varyant, stok, fiyat, vergi, kargo ve ödeme koşullarını doğrular.",
      "`prepare_order_cancellation` müşterinin KENDİ siparişinin iptal edilebilirliğini ve sonuçlarını hesaplar.",
      "Teklif tek bir mağaza, kişi, konuşma ve ürün/tutar bileşimine bağlıdır; içeriğinin parmak izini, sürümünü ve son kullanma zamanını taşır.",
      "Varsayılan geçerlilik 10 dakikadır. Hazırlama adımı hiçbir şey oluşturmaz ve hiçbir şeyi iptal etmez.",
      "Halka açık kanallarda (yorum, marketplace sorusu) yalnız ürün keşfi yapılabilir; sipariş/iptal akışı özel kanallara sınırlıdır."
    ],
    inputs: [
      "Doğrulanmış bilgiler (ürün, adet, adres, kargo, ödeme)",
      "Canlı katalog/kurallar",
      "Kişi bağlamı"
    ],
    outputs: [
      "Sipariş/iptal teklifi",
      "İçerik parmak izi + sürüm",
      "Son kullanma zamanı",
      "Müşteriye gösterilecek onay özeti"
    ],
    codeRefs: [
      "qpien-shop-v2/app/src/modules/CommerceMcp/server/commerceMcpServer.ts:281",
      "qpien-shop-v2/app/src/modules/CommerceMcp/services/commerceOrders.service.ts:180"
    ],
    channels: ["all", "commerce"],
    position: { x: 6120, y: -700 }
  },
  {
    id: "commerce-approval",
    title: "Exact approval challenge doğrulanır",
    shortTitle: "Approval gate",
    subtitle: "Buton ya da tek kullanımlık kod olmadan write yoktur.",
    kind: "security",
    stage: "Approval",
    productSummary:
      "Doğal dilde “evet” sipariş oluşturmaz. Müşteri, ekranda gördüğü teklifin kendisine bağlı bir onay verir: destekleyen kanalda buton, diğerlerinde teklife özel tek kullanımlık `QPN-XXXXXXXX` kodu.",
    details: [
      "Onay tek bir teklife bağlıdır: teklif kimliği/parmak izi/sürümü, kişi, konuşma, teklifi gösteren mesaj, onay mesajı, hedef işlem, son kullanma zamanı ve tek kullanımlık nonce.",
      "Onay kaydı modele görünmez; MCP isteğinin metadata'sında taşınır.",
      "Fiyat, stok, ürün, varyant, adet, adres, kargo, ödeme veya toplam değişirse teklif ve eski onay iptal edilir; süreç yeni bir teklif hazırlar.",
      "Süresi geçmiş veya daha önce kullanılmış bir onay reddedilir.",
      "Teklifi gösteren mesajın id'si veritabanına yazıldıktan sonra workflow'a bağlanır (CAS).",
      "⚠️ Güvenlik notu: onay kaydı şu an `base64url` ile kodlanmış düz JSON + şema kontrolüdür; kriptografik olarak İMZALI DEĞİL (önceden RSA imzalıydı). Bu alanları öğrenebilen biri geçerli görünen bir onay üretebilir."
    ],
    inputs: [
      "Teklif özeti",
      "Buton payload'ı veya tek kullanımlık kod",
      "Teklif/onay mesaj id'leri"
    ],
    outputs: [
      "Kısa ömürlü onay kaydı (imzasız)",
      "Reddedilen/tekrar kullanılan onay",
      "Yeniden hazırlama isteği"
    ],
    decisionOutcomes: [
      "Exact valid challenge -> host-only write",
      "Natural language approval -> WAITING_APPROVAL",
      "Changed proposal -> invalidate + prepare again",
      "Expired/replayed nonce -> reject"
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/commerceWorkflowOrchestrator.service.ts:1770",
      "qpien-shop-v2/app/src/modules/CommerceMcp/services/commerceApproval.service.ts:22"
    ],
    channels: ["all", "commerce"],
    position: { x: 6480, y: -700 }
  },
  {
    id: "commerce-write",
    title: "Host-only write atomik yürütülür",
    shortTitle: "Approved write",
    subtitle:
      "place_order / cancel_my_order yalnız orchestrator tarafından çağrılır.",
    kind: "transaction",
    stage: "MCP transaction",
    productSummary:
      "Doğrulanmış approval sonrasında API-v2 ayrı write scope ve idempotency key üretir; model transaction tool'unu asla çağırmaz.",
    details: [
      "Modele `place_order` / `cancel_my_order` HİÇ verilmez; bu çağrıyı yalnız host orchestrator yapar.",
      "Shop server onayı ve teklifi yeniden doğrular, nonce'ı tek kullanımlık tüketir.",
      "Proposal PostgreSQL transaction/row lock ile atomik claim edilir.",
      "Aynı idempotency key + aynı request hash mevcut sonucu döndürür; farklı hash conflict olur.",
      "Order ownership actor subject ve doğrulanmış identity link ile korunur.",
      "Non-idempotent platform mutation sonucunun belirsiz olduğu durumda otomatik ikinci create/cancel yapılmaz."
    ],
    inputs: [
      "Write yetkisi (host tarafından üretilen scope)",
      "Onay kaydı",
      "Teklif id'si",
      "Idempotency key"
    ],
    outputs: [
      "Exactly-once local operation",
      "Order/cancel result",
      "PENDING_RECONCILIATION"
    ],
    codeRefs: [
      "qpien-shop-v2/app/src/modules/CommerceMcp/server/commerceMcpServer.ts:317",
      "qpien-shop-v2/app/src/modules/CommerceMcp/services/commerceOrders.service.ts:437"
    ],
    channels: ["all", "commerce"],
    position: { x: 6840, y: -700 }
  },
  {
    id: "commerce-reconciliation",
    title: "Belirsiz platform sonucu uzlaştırılır",
    shortTitle: "Reconciliation",
    subtitle: "Zaman aşımında körlemesine tekrar yok; sonuç aranır.",
    kind: "transaction",
    stage: "Recovery",
    productSummary:
      "Sipariş karşı tarafta gerçekten oluşmuş ama cevabı bize ulaşmamış olabilir. Bu durumda ikinci bir sipariş DENENMEZ: işlemin kendi referansıyla sonucu aranır ve süreç güvenli biçimde tamamlanır.",
    details: [
      "Sonucu belirsiz kalan bir yazma işlemi süreci 'uzlaştırılıyor' (RECONCILING) durumuna alır.",
      "`get_operation_status` yalnız host'un kullandığı bir tool'dur; modele verilmez.",
      "Uzlaştırma, platform referansıyla uzak siparişi arar ve yereldeki işlem/sipariş kaydıyla eşler.",
      "Servis yeniden başlasa bile aynı işlem referansı ve idempotency key üzerinden kaldığı yerden devam eder.",
      "Çözülemeyen geçici hata `FAILED_RECOVERABLE` olarak görünür — müşteriye yeni bir sipariş yaratılmaz."
    ],
    inputs: ["İşlem id'si", "Platform referansı", "Idempotency key"],
    outputs: [
      "Başarılı / başarısız sonucu",
      "Kurtarılan platform sonucu",
      "FAILED_RECOVERABLE"
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/commerceWorkflowOrchestrator.service.ts:268",
      "qpien-shop-v2/app/src/modules/CommerceMcp/services/commerceReconciliation.service.ts:42"
    ],
    channels: ["all", "commerce"],
    position: { x: 7200, y: -1000 }
  },
  {
    id: "commerce-response-policy",
    title: "Commerce cevap politikası uygulanır",
    shortTitle: "Response policy",
    subtitle:
      "Keşif finalize edilir; finansal ve approval metni exact korunur.",
    kind: "decision",
    stage: "Commerce output",
    productSummary:
      "İkinci LLM'in finansal/approval/transaction metnini değiştirmemesi için commerce cevabı açık bir finalization politikasıyla ana pipeline'a döner.",
    details: [
      "STANDARD_FINALIZE ürün keşfi ve recoverable fallback gibi normal cevapları FINALIZE_AI_CONTENT'e gönderir.",
      "PRESERVE_EXACT eksik alan sorusu, proposal özeti, approval, fiyat değişimi ve transaction sonucunu ikinci LLM'e sokmadan gönderir.",
      "NO_RESPONSE yalnız duplicate/in-flight mesajın tekrar cevaplanmaması gereken durumda kullanılır.",
      "`controlAndPay` seçilen policy sonrasında tam bir kez çalışır.",
      "Auto-close müşteri AI profile tercihi olarak ayrı kalır; commerce sonucu tarafından değiştirilmez.",
      "İlk commerce mesajında STANDARD_FINALIZE cevabı, paralel çalışan RAG sonucuyla birleştirilmiş olabilir (bkz. 'RAG + Commerce merge'); devam mesajlarında (workflow resume) böyle bir paralel RAG çağrısı yoktur."
    ],
    inputs: ["Commerce answer", "Workflow status/step", "expectsInput"],
    outputs: [
      "STANDARD_FINALIZE",
      "PRESERVE_EXACT",
      "NO_RESPONSE",
      "Commerce metadata"
    ],
    decisionOutcomes: [
      "STANDARD_FINALIZE -> FINALIZE_AI_CONTENT",
      "PRESERVE_EXACT -> send + memory",
      "NO_RESPONSE -> outbound message yok"
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/qpienAi.service.ts:1056",
      "api-v2/app/src/modules/QpienAi/core/aiProfile.types.ts:118"
    ],
    channels: ["all", "commerce"],
    position: { x: 5400, y: -560 }
  },
  {
    id: "commerce-rag-merge",
    title: "Commerce ve RAG cevabı birlikte sunulur",
    shortTitle: "RAG + Commerce merge",
    subtitle:
      "Ürün keşfi cevabına, arka planda paralel çalışan bilgi tabanı cevabı otomatik eklenebilir.",
    kind: "decision",
    stage: "Merge",
    productSummary:
      "Kullanıcı bir commerce mesajı yazdığında AI artık arka planda aynı anda hem ürün/sipariş akışını hem bilgi tabanını sorar; commerce cevabı normal bir keşif/fallback cevabıysa iki cevap tek mesajda birleştirilir, kullanıcı ekstra beklemez.",
    details: [
      "Yalnız ilk commerce mesajında çalışır: RAG ve commerce workflow'u aynı anda (`Promise.all`) başlatılır.",
      "Commerce cevabı boşsa, insan desteğine düşmüşse veya PRESERVE_EXACT/NO_RESPONSE ise (onay, sipariş sonucu, eksik bilgi sorusu gibi) birleştirme atlanır; yalnız commerce cevabı kullanılır — bu davranış özellik eklenmeden önceki hale özdeştir.",
      "Birleştirme uygunsa iki içerik alt alta eklenir (boş satırla ayrılır) ve kaynaklar `ragAndCommerce` olarak finalize adımına taşınır.",
      "Bu, `ragAndAction` ile aynı finalize-seviyesi uzlaştırma mantığını paylaşır ama tetikleyici tamamen commerce'e özeldir; workflow devam (resume) mesajlarında bu paralel RAG çağrısı hiç yapılmaz."
    ],
    inputs: ["Paralel RAG cevabı", "Commerce cevabı (responsePolicy dahil)"],
    outputs: [
      "Birleştirilmiş cevap (RAG + commerce)",
      "Yalnız commerce cevabı (PRESERVE_EXACT/insan desteği/boş içerikte)"
    ],
    decisionOutcomes: [
      "STANDARD_FINALIZE + içerik var + humanSupport değil -> RAG paragrafı eklenir",
      "PRESERVE_EXACT / NO_RESPONSE / humanSupport / boş içerik -> yalnız commerce cevabı"
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:481",
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:669",
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:718"
    ],
    channels: ["all", "commerce"],
    position: { x: 5760, y: -560 }
  },
  {
    id: "commerce-audit",
    title: "Commerce kararları step audit'e yazılır",
    shortTitle: "Commerce audit trail",
    subtitle:
      "Workflow, agent, MCP tool, rerank ve failure adımları izlenebilir.",
    kind: "audit",
    stage: "Observability",
    productSummary:
      "Admin AI Logs step drawer'ında intent'ten sonra Commerce workflow'un hangi tool'u neden çağırdığı ve nasıl sonuçlandığı görülebilir.",
    details: [
      "COMMERCE_WORKFLOW_START yeni state girişini; COMMERCE_WORKFLOW_RESUME aktif işlem devamını veya tamamlanmış discovery'den devralınan ürün referansını gösterir.",
      "COMMERCE_SLOT_EXTRACTION ilk intent/slot kararını ya da takip mesajının COMMERCE_SIDE_QUESTION/NEW_TOPIC sınıflandırmasını ve nedenini gösterir.",
      "COMMERCE_PAGE_PRODUCT_RESOLVE bulunduğu sayfadan ürün çözümlemesini gösterir — atlanan durumlar dahil (`NO_PAGE_URL`, `CACHE_HIT`, `CACHE_HIT_NO_MATCH`, `CACHE_UNAVAILABLE`), çünkü sessiz atlama run'ı kendi izinden teşhis edilemez hale getiriyordu.",
      "COMMERCE_MARKETPLACE_PRODUCT_RESOLVE marketplace sorusunun ilanının katalogda bulunup bulunmadığını gösterir: `MATCHED`, `MATCHED_NOT_SELLABLE`, `NO_MATCH`, `AMBIGUOUS`, `NO_MARKETPLACE_SHOP`, `NO_REFERENCE`, atlama nedeni (`PLATFORM_NOT_SYNCED`) ve hangi kolondan eşleştiği (`matchedOn`).",
      "Ürün metnini HANGİ kaynağın verdiği ayrı adımlarla okunur: `GET_QPIEN_SHOP_PRODUCT_DETAILS` = kendi kataloğumuz, `MARKETPLACE_REQUEST` = canlı pazaryeri API'si (fallback). İkisi eskiden aynı adı taşıyordu ve katalogdan cevaplanan her run API'ye gitmiş gibi görünüyordu — sıralamanın var olma sebebi tam olarak bu karışıklığı kaldırmak.",
      "COMMERCE_MCP_LIST_TOOLS yetki/allowlist'i; COMMERCE_AGENT_ROUND model kararını gösterir.",
      "COMMERCE_AGENT_ROUND ayrıca güncel tool kanıtının zorunlu olup olmadığını ve modele iletilen maskelenmiş tool sonuçlarını gösterir; host'un ön-çektiği ürün detayı `request.hostPrefetch: true` ile ayrılır.",
      "COMMERCE_MCP_TOOL_CALL sanitize edilmiş tam args/result'u; COMMERCE_PRODUCT_RERANK sıralamayı ve 'istenen ürün' kararını gösterir.",
      "COMMERCE_GUIDANCE_ENFORCEMENT hazır cevabın PM kurallarına göre yeniden şekillendirilip şekillendirilmediğini gösterir.",
      "COMMERCE_ERROR recoverable failure kodu ve resultingStatus alanını gösterir.",
      "Aynı zaman çizelgesinde AI_ROLE_RESOLUTION, SKILL_ROUTING ve SKILL_AGENT adımları da bulunur: bir mesajın hangi role gittiği, Skill'e girip girmediği ve commerce'in ne yaptığı tek run içinde okunur.",
      "Token, onay kodu/hash, e-posta, telefon, adres ve checkout URL audit/loglarda recursive redaction ile maskelenir."
    ],
    inputs: [
      "Run id",
      "Step request/response",
      "Duration",
      "Workflow metadata"
    ],
    outputs: [
      "Queryable AI step events",
      "Sanitized MCP audit",
      "Failure diagnosis"
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/core/aiProfile.enums.ts:48",
      "api-v2/app/src/modules/QpienAi/services/commerceAgentRunner.service.ts:121",
      "qpien-shop-v2/app/src/modules/CommerceMcp/services/commerceMcpAudit.service.ts"
    ],
    channels: ["all", "commerce"],
    position: { x: 4320, y: -1440 }
  },
  {
    id: "close-conversation",
    title: "Otomatik kapanış ayrı akış",
    shortTitle: "Close conversation",
    subtitle: "Yanıtsız konuşmada kapanış mesajı üretilebilir.",
    kind: "prompt",
    stage: "Auxiliary",
    productSummary:
      "AI auto-close aktifse kapanış mesajı önce `CLOSE_CONVERSATION`, sonra final prompt ile düzenlenir.",
    details: [
      "Bekleme süresi dolan AI konuşmaları taranır.",
      "Kapanış action message'ı ve son konuşma geçmişiyle kapanış cevabı üretilir.",
      "Cevap yine `FINALIZE_AI_CONTENT` ile son hale getirilir."
    ],
    inputs: [
      "Auto-close action message",
      "Conversation history",
      "Detected language"
    ],
    outputs: ["Closing answer", "Finalized closing message"],
    promptNames: [
      {
        name: "CLOSE_CONVERSATION",
        usage: "Otomatik kapanış mesajını üretir.",
        placeholders: [
          {
            token: "{{actionMessage}}",
            meaning: "AI profile auto-close mesajı"
          },
          { token: "{{language_code}}", meaning: "Kullanıcının son dili" }
        ]
      },
      {
        name: "FINALIZE_AI_CONTENT",
        usage: "Kapanış mesajını da marka tonuna göre sonlandırır."
      }
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/qpienAi.service.ts:320"
    ],
    channels: ["all", "chat"],
    position: { x: 5760, y: 420 }
  },
  {
    id: "role-resolution",
    title: "Hangi AI rolü cevaplayacak",
    shortTitle: "AI role resolution",
    subtitle: "Bir müşteri birden çok AI rolünü aynı anda çalıştırabilir.",
    kind: "decision",
    stage: "Routing",
    productSummary:
      "Bir müşteri Servis, E-ticaret gibi birden fazla AI rolünü açabilir (her rol ayrı bir AiProfile dokümanı). Bu adım, bu turu hangi rolün cevaplayacağını belirler; prompt, guidance, yetkiler ve ücretlendirme o rolden okunur.",
    details: [
      "AI cevap verecek mi kararı ayrıdır ve BİRLEŞİK kapıdır: rollerden HERHANGİ BİRİ bu kanal, mesaj tipi ve çalışma saati için uygunsa AI devreye girer (`controlAiProcess` / `resolveEligibleAiProfiles`).",
      "Tur, konuşmanın daha önce oturduğu rolle (veya yoksa Servis / tek uygun rolle) GEÇİCİ olarak başlar; nihai karar burada verilir.",
      "Tek uygun rol varsa hiçbir karar verilmez, LLM'e hiçbir şey sorulmaz ve hiçbir şey yazılmaz.",
      "Enrich'in `businessRole` sınıflandırması emin değilse geçici rol yalnız bu tur için cevaplar; sonraki mesaj yeniden karar verir.",
      "Enrich emin ve konuşma henüz oturmamışsa o rol cevaplar ve konuşma o role OTURUR.",
      "Enrich emin ama konuşma BAŞKA bir role oturmuşsa rol değişimi yalnız `NEW_TOPIC` turlarında ve yalnız YETENEK KAZANDIRAN yöne yapılır (`switchAddsCapability`). NEW_TOPIC koşulu olmadan ürün sorup sonra kargo soran bir müşteri roller arasında zıplar ve commerce workflow'unu kaybederdi; yön koşulu olmadan ise \"müşteri hizmetlerine bağla\" tek başına oturmuş bir alışveriş sohbetini Servis'e taşıyıp kalan bütün turların katalog erişimini götürüyordu. Role bağlı tek yetenek commerce olduğu için E-ticaret üstkümedir; engellenen geçiş audit'e `KEPT_SETTLED_NO_CAPABILITY_GAIN` olarak yazılır.",
      "Commerce yetkisi role bağlıdır: aktif e-ticaret entegrasyonu + rolün commerce'e izin vermesi gerekir. Hiç ikinci rol açmamış müşteri eski davranışını korur.",
      "Bilgi tabanı bilinçli olarak role göre daraltılmaz: `rag` yeteneği yalnız müşteriye göre filtreler, aksi halde ikinci rol bütün bilgi tabanını göremiyordu.",
      "Cevap veren rol pipeline dışına `answer.resolvedAiRole` olarak çıkar; finalize stili, guidance, ücretlendirme, cevap butonları, marketplace cevap modu ve auto-close sayacı bu role göre çalışır (`pickAnsweringProfile`).",
      "Her tur audit'e `AI_ROLE_RESOLUTION` adımı yazılır — tek rollü müşterilerde bile. Uygun roller, oturmuş rol, enrich'in sınıflandırması, hangi kuralın çalıştığı ve rolün açtığı yetkiler kaydedilir; admin adım çekmecesinde run özeti olarak da görünür.",
      "Rol prompt satırı ve intent yetkileri enrich ile paralel değil, rol kararından SONRA okunur: prompt rolün kendi metnidir, yetkiler ise intent şemasının hangi seçenekleri sunacağını belirler. İkisi de cache'li okuma olduğu için maliyeti yok sayılır.",
      "Yayına alma sırası: iki tek seferlik migration (`MIGRATE_AI_PROFILE_ROLES`, `MIGRATE_AI_PROMPT_ROLES`) çalışmadan müşteri ikinci rol açamaz."
    ],
    inputs: [
      "Eligible AI profiles",
      "Provisional AI profile",
      "AiPersonMemory.isAiRoleSettled",
      "Enrich businessRole + contextMode"
    ],
    outputs: [
      "params.aiProfile (rol kapsamlı)",
      "answer.resolvedAiRole",
      "Intent capabilities",
      "AI_ROLE_RESOLUTION audit step"
    ],
    decisionOutcomes: [
      "Tek uygun rol -> karar yok",
      "Enrich emin değil -> geçici rol, konuşma oturmaz",
      "Enrich emin + oturmamış -> o rol cevaplar ve oturur",
      "Enrich emin + başka role oturmuş -> yalnız NEW_TOPIC'te rol değişir"
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:74",
      "api-v2/app/src/modules/QpienAi/services/askQuestion.pipeline.ts:118",
      "api-v2/app/src/modules/QpienAi/core/aiRole.utils.ts",
      "api-v2/app/src/modules/QpienAi/core/qpienAi.constants.ts"
    ],
    channels: [
      "all",
      "skills",
      "commerce",
      "chat",
      "comment",
      "question",
      "privateReply"
    ],
    position: { x: 2880, y: 120 }
  },
  {
    id: "commerce-turn-context",
    title: "Turun konusu belirlenir",
    shortTitle: "Turn subject + page product",
    subtitle: "“Bu ürün” derken hangi ürün? Cevabı host bulur, model değil.",
    kind: "context",
    stage: "Commerce context",
    productSummary:
      "Müşteri “bunun fiyatı ne?” dediğinde konunun ne olduğunu modele tahmin ettirmek yerine host çözer: canlı sohbette bakılan sayfanın ürünü, yorum/DM'de gönderinin kendisi, marketplace sorusunda ürün kartı.",
    details: [
      "Canlı sohbette hafızadaki sayfa adresi bir ürün sayfasına işaret ediyorsa host `resolve_product_by_url` tool'unu çağırır ve bulunan ürün id'sini agent'a güvenilir referans olarak verir. Bu tool modele HİÇ verilmez.",
      "Marketplace sorusunda (messageType QUESTION) sayfa adresi yoktur; sorunun sorulduğu ilan vardır. Host, commerce lane'inin İÇİNDE (pipeline'dan önce değil — orada çalıştırmak ürün metnini intent prompt'una sokup turu commerce'ten çıkarıyordu) `resolve_product_by_marketplace_ref` ile ilanı kendi kataloğumuzda çözer: sadece MARKETPLACE kategorisindeki mağazalar, ürün kanalı Trendyol ise sadece Trendyol, Hepsiburada ise sadece Hepsiburada. Bu tool da modele HİÇ verilmez.",
      "İlan referansının hangi kolonda durduğu platform bilgisidir, katalog bilgisi değil: Hepsiburada sorusu `hbSku` taşır ve o `item_id`'dedir (`sku` merchantSku'dur). Sıra her platformun kendi products driver'ındaki `listingRefFields` ile gelir.",
      "⚠️ Trendyol sorusu, ürün API'sinin aldığı `productMainId`'yi TAŞIMAZ. Canlı ölçüm (satıcı 133486, run 6a97d78643fa877e4cb6de0c): soru `productMainId: 869035657` taşıyor, bu kendi `productUrl`'ündeki content id (`.../xyz-p-869035657`); katalogda ise satıcının kendi kodu (`C0-S0023-00003`) `external_product_id`'de duruyor — o kiracının 12.560 Trendyol varyantının sadece 16'sının numerik `external_product_id`'si var. Bu yüzden Trendyol'da önce `sku` (stockCode), o da tutmazsa `item_url` içindeki `-p-<id>` parçasını çıkaran `itemUrl` katmanı devreye girer.",
      "Bir referans tek varyantı değil TÜM ilanı adlandırabilir (Hepsiburada'nın variantGroupId'si, URL content id'si). Bu yüzden 'birden fazla eşleşme' ÜRÜN sayısıyla değil İLAN sayısıyla ölçülür: aynı ilanın birkaç varyantı ilanı çözer (varyant seçilmez); farklı iki ürün, aynı platformda iki mağaza, VEYA aynı ürünün iki farklı ilanı ise AMBIGUOUS'tur. Sonuncusu istisna değil kuraldır — bir Trendyol `productMainId`'si makalenin bütün renk ilanlarını birden eşler (bkz. 'Cevaplanabilir birim ilandır').",
      "'Satışta değil' bir eşleşmedir, kayıp değil: `MATCHED_NOT_SELLABLE` ilanı id'leriyle döner ve `subjectSellable: false` olarak agent'a geçer. Bu bayrak EŞLEŞEN varyanttan değil İLANDAN hesaplanır — Trendyol sorusu tek varyantı adlandırdığı için, stokta olmayan bir bedeni soran müşteride tüm ilan 'satışta değil' görünüyordu — agent o ilanı inceleyip 'şu anda satışta değil' der, başka ürün aramaya gitmez. Sadece soft-delete edilmiş varyant hâlâ NO_MATCH sayılır (bizdeki kopya silinmiş, sadece marketplace bilir). Bu istisna değil kural: canlı bir kiracının Trendyol master'larının %89'u DRAFT (2.035'in 1.809'u). DRAFT bir ürünün metnini okumak host'un verdiği `allowInactiveProduct` iznini gerektirir; `search_products` yine sadece ACTIVE döner.",
      "Devralınan bir keşif hiçbir ürün adlandırmıyorsa (sadece `sourceWorkflowId`, boş `candidateIds`) turun kendi konusunu bastıramaz — `discoveryReferenceContext` bunu referans saymaz, `mergeWorkflowContext` iki tarafı birleştirir. Aksi hâlde agent round 0'a hiçbir referans olmadan giriyor ve katalogda 'Bu ürün' diye arama yapıyordu.",
      "Çözülen ilan turun katalog okumalarını ÜÇ kademede kısıtlar — mağaza (`x-qpien-shop-scope`), ilan (`x-qpien-listing-scope`) ve makale (`x-qpien-listing-group-scope`) — artı sorulan varyantı işaretleyen `x-qpien-subject-variant`. Hepsi header, tool argümanı DEĞİL: modelin genişletebileceği bir alan bu işi yapamaz. Mağaza kademesi tek başına yetmez, çünkü aynı mağazadaki kardeş renk ilanları da tek master'a çöker — ayrıntı için 'Cevaplanabilir birim ilandır' düğümüne bakın.",
      "Katalog çözemezse (platform qpien-shop-v2'ye senkron değil, ilan soft-delete edilmiş, referans belirsiz veya MCP hatası) MARKETPLACE_REQUEST katmanına düşülür: canlı marketplace API'si. O katman METİN üretir, id üretmez — agent'a geçebilecek güvenilir bir referans çıkmaz, ve commerce lane'i zaten çalışmaz (fail-closed). Trendyol tarafı Product **v2**'dir: v1 `/products` planlı brownout ile 426 UPGRADE_REQUIRED dönüyor ve her marketplace sorusunda 2,7 saniyeyi boşa harcıyordu.",
      "Öncelik nettir: konuşmada zaten seçilmiş bir ürün/aday varsa sayfa ürünü tamamen bastırılır — müşterinin BAKTIĞI şey, KONUŞTUĞU şeyi ezmez.",
      "Yorum ve private reply turunda gönderi başlığı/URL'si, marketplace sorusunda ürün adı/URL'si 'turun konusu ipucu' olarak taşınır. Bu ipucu RAG tarafının zaten aldığı kanal zarfından okunur, yani kanal bazlı yeni bir kod yolu açılmaz.",
      "İpucu modele `UNTRUSTED_TURN_SUBJECT` olarak, güvenilir id'lerden AYRI bir blokla verilir: id'lere host kefil olur ve tool'a geçebilir, ipucu ise başkalarının yazdığı halka açık metindir.",
      "Sonuç sıfır olsa bile her deneme audit'e yazılır; sessiz atlama run'ı teşhis edilemez yapıyordu. Sonuçlar (olumsuzlar dahil) Redis'te cache'lenir.",
      "Bu blok, RAG'in gönderiyi görüp doğru cevap verdiği halde commerce'in çıplak cümleyi katalogda araması (ör. 'Fiyat bilgisi' diye ürün arama) sorununu kapatır."
    ],
    inputs: [
      "aiPersonMemory.pageUrl",
      "extraData.marketplaceSubject (QUESTION turunda host'un çözdüğü ilan)",
      "Kanal zarfı (post/ürün detayı)",
      "Workflow slot/aday durumu"
    ],
    outputs: [
      "workflowContext.currentPageProductId",
      "workflowContext.subjectSource (STOREFRONT_PAGE | MARKETPLACE_LISTING)",
      "workflowContext.subjectHint",
      "MCP oturumunun kapsamı (shop / listing / listingGroup / subjectVariant header'ları)",
      "COMMERCE_PAGE_PRODUCT_RESOLVE audit step",
      "COMMERCE_MARKETPLACE_PRODUCT_RESOLVE audit step"
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/commerceWorkflowOrchestrator.service.ts",
      "api-v2/app/src/modules/QpienAi/services/marketplaceQuestionSubject.service.ts",
      "api-v2/app/src/modules/QpienAi/core/marketplaceQuestion.constants.ts",
      "api-v2/app/src/modules/QpienAi/core/channelScope.utils.ts",
      "api-v2/app/src/modules/QpienAi/cache/commercePageProduct.cache.ts",
      "api-v2/app/src/modules/QpienAi/core/commerce.constants.ts:202",
      "qpien-shop-v2/app/src/modules/Products/services/productDiscovery.service.ts"
    ],
    channels: ["all", "commerce"],
    position: { x: 4680, y: -1180 }
  },
  {
    id: "commerce-marketplace-lane",
    title: "Marketplace turu commerce turundan FARKLI çalışır",
    shortTitle: "Marketplace ≠ Commerce",
    subtitle:
      "Aynı MCP, farklı tool listesi ve farklı kısıtlar. Bunlar prompt tavsiyesi değil, yapısal.",
    kind: "guard",
    stage: "Marketplace lane",
    productSummary:
      "Canlı sohbette müşteri katalogda arama yaptırabilir — “kırmızı elbise var mı” diye sorar, AI arar. Marketplace'te (Trendyol/Hepsiburada soruları) böyle değil: soru zaten BİR ilanın altında sorulmuştur, cevap o ilanın sayfasında yayınlanır ve platform kuralları başka ürün adı geçmesini yasaklar. Bu yüzden marketplace turunda AI'ın katalogda arama yapma yeteneği hiç verilmez — “arama yapma” diye söylenmez, arama aracı listeye konmaz.",
    details: [
      "İki lane'in farkı tek satırda: `marketplaceQnaChannel && step === DISCOVERY` ise tool seti `COMMERCE_MARKETPLACE_STEP_TOOLS`, değilse `COMMERCE_STEP_TOOLS[step]`.",
      "Commerce (storefront) DISCOVERY tool'ları: resolve_catalog_context, search_products, get_product_details, get_product_variants, compare_products, get_product_policies, get_size_guide.",
      "Marketplace DISCOVERY tool'ları: get_product_details, get_product_variants, get_listing_group_variants, get_product_policies, get_size_guide. `search_products`, `resolve_catalog_context` ve `compare_products` LİSTEDE YOKTUR — üçü de ancak müşterinin sormadığı bir ürün hakkında cevap üretebilir.",
      "Neden yapısal: run 6a9973e6c96ca00d229037ea'da ilan çözülemedi, model elindeki `search_products`'a uzandı, 6,5 saniyede 10 ürün getirdi ve bir babet ilanına sorulan beden sorusunu BAŞKA dört ilanın bedenleriyle cevapladı. “Başka ürün adı geçirme” kuralı zaten prompt'ta yazılıydı; eksik olan, o ürünü bulabildiği araçtı.",
      "FAIL-CLOSED: ilan çözülemezse commerce lane HİÇ çalışmaz (`canRunMarketplaceCommerce`). Tur RAG hattına bırakılır; RAG'in de cevabı yoksa cevap üretilmez. Marketplace'te tek hakkın var ve yanlış ürünü anlatmak, hiç cevap vermemekten kötüdür.",
      "Görselden ürün tanıma marketplace'te YOKTUR: indeks yalnızca E-ticaret rolünün storefront mağazalarını içerir ve marketplace turu ilan görseline hiç bakmaz. İlan çözülemezse yukarıdaki fail-closed kuralı aynen geçerlidir.",
      "Atlama sessiz değildir: `COMMERCE_MARKETPLACE_PRODUCT_RESOLVE` adımına `MARKETPLACE_COMMERCE_SKIPPED` olarak yazılır ve cevap `commerceNoAnswer` ile işaretlenir — böylece paralel RAG cevabı yayınlanabilir (düz NO_RESPONSE “zaten cevaplandı” anlamına gelir ve RAG'i çöpe atardı).",
      "Marketplace cevabına ayrıca `MARKETPLACE_QNA_ANSWER_RULES` eklenir: link yok, iletişim bilgisi yok, başka ürün yok, sorulmayanı söyleme. Bunlar mağaza ayarlarını ve persona'yı EZER — platform uyum kuralıdır, ton tercihi değil.",
      "⚠️ Değişiklik yapacaklara: bir tool'u `COMMERCE_MARKETPLACE_STEP_TOOLS`'a eklemek, o tool'un marketplace cevabında kullanılabileceği anlamına gelir. Katalog geneline erişen hiçbir tool bu listeye girmemelidir."
    ],
    inputs: [
      "extraData.msgType (QUESTION)",
      "Çözülmüş ilan referansı (productId + shopId)",
      "CommerceWorkflowStep"
    ],
    outputs: [
      "Kısıtlı tool listesi (5 tool)",
      "MARKETPLACE_QNA_ANSWER_RULES prompt bloğu",
      "commerceNoAnswer (ilan çözülemediğinde)"
    ],
    decisionOutcomes: [
      "İlan çözüldü → marketplace tool seti ile agent çalışır",
      "İlan çözülemedi → commerce atlanır, tur RAG'e bırakılır"
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/core/commerce.constants.ts (COMMERCE_MARKETPLACE_STEP_TOOLS)",
      "api-v2/app/src/modules/QpienAi/services/commerceAgentRunner.service.ts (allowed set)",
      "api-v2/app/src/modules/QpienAi/services/commerceWorkflowOrchestrator.service.ts (canRunMarketplaceCommerce)",
      "api-v2/app/src/modules/QpienAi/core/marketplaceQuestion.constants.ts"
    ],
    channels: ["all", "commerce", "question"],
    position: { x: 5040, y: -1180 }
  },
  {
    id: "commerce-listing-scope",
    title: "Marketplace'te cevaplanabilir birim ÜRÜN değil İLANDIR",
    shortTitle: "Listing / article scope",
    subtitle:
      "Bizim `products` satırımız birden çok ilanı birleştirir; marketplace cevabı tek ilana kilitlenir.",
    kind: "security",
    stage: "Marketplace lane",
    productSummary:
      "Qpien Shop'ta bir “ürün”, aynı malın Shopify'daki, Trendyol'daki ve mağazanın kendi sitesindeki kopyalarını TEK satırda birleştirir — mağaza araması için doğru olan budur. Ama marketplace'te müşteri tek bir ilanın altında soruyor ve o ilanın kendi stoğu, kendi fiyatı var. Ürün satırından cevaplamak, müşterinin bakmadığı sayfaların stoğunu ona söylemek demektir.",
    details: [
      "Ölçülmüş örnek: `fec1deb9…` tek master satırı 2 mağaza · 9 ilan · 59 varyant taşıyor — 4 Trendyol renk ilanı + 5 storefront sayfası. “Hangi beden stokta var?” sorusu bu satırdan cevaplanınca çıkan sonuç dört ilanın BİRLEŞİMİ oldu: “19, 20, 21, 22, 23 ve 24”. Sorulan ilanda stokta olan tek beden 23'tü (run 6a9973e6c96ca00d229037ea).",
      "Üç kademeli kısıtlama var ve üçü de HEADER'dır, tool argümanı değildir — model göremediği bir alanı genişletemez:",
      "① `x-qpien-shop-scope` → hangi mağaza. Trendyol sorusuna Shopify fiyatı cevaplanmasını engeller.",
      "② `x-qpien-listing-scope` → hangi İLAN. Aynı mağaza içindeki kardeş renk ilanlarını keser. Anahtar `listing_key`: Trendyol'da productContentId (productUrl'deki `-p-<id>`), Hepsiburada'da variantGroupId, storefront'ta ürün id'si.",
      "③ `x-qpien-listing-group-scope` → hangi MAKALE (productMainId). Sadece renk sorusu gibi kardeş ilanlara bakmayı gerektiren durumda `get_listing_group_variants` bu kapsamla çalışır.",
      "Ayrıca `x-qpien-subject-variant`: sorunun sorulduğu TAM varyant. Bu bir filtre değil işarettir — o satır `isSubject: true` ile döner. Filtre olsaydı “hangi bedenler var” cevaplanamaz olurdu.",
      "Referans tek bir ilana kilitlenemiyorsa sonuç AMBIGUOUS'tur, tahmin değil. Trendyol `productMainId`'si 4 renk ilanının hepsiyle eşleşir; bu artık “eşleşti” sayılmaz. Eşleşme ölçüsü ÜRÜN sayısı değil İLAN sayısıdır.",
      "`listing_key` kolonu boşsa SQL'den türetilir (`item_url` → `external_product_id` → `item_id`). Kolona bağımlılık bir kez üretimi kırdı: mağaza yeniden bağlanıp 12.560 varyant NULL anahtarla yazıldı, her çözümleme AMBIGUOUS döndü ve marketplace soruları hiç cevap alamadı (run 6a9a851e477bfd19d973789c).",
      "`sellable` bayrağı EŞLEŞEN varyanttan değil İLANDAN hesaplanır: Trendyol sorusu tek varyantı adlandırdığı için, stokta olmayan bir bedeni soran müşteride tüm ilan “satışta değil” görünüyordu.",
      "⚠️ Değişiklik yapacaklara: scope header'ları merchant metni taşır (gerçek productMainId'ler boşluk ve Türkçe harf içeriyor — “C0-S0002-00051 Şırıngıl Laci”). Bu yüzden percent-encoded gönderilir ve sunucuda karakter beyaz listesi DEĞİL uzunluk sınırı vardır. Beyaz liste koymak o makalelerdeki her soruyu 401'e düşürür."
    ],
    inputs: [
      "resolve_product_by_marketplace_ref sonucu (productId, variantId, shopId, listingKey, listingGroupKey)",
      "product_variants.listing_key (veya SQL türevi)"
    ],
    outputs: [
      "x-qpien-shop-scope",
      "x-qpien-listing-scope",
      "x-qpien-listing-group-scope",
      "x-qpien-subject-variant",
      "offers[].isSubject"
    ],
    codeRefs: [
      "qpien-shop-v2/app/src/modules/CommerceMcp/core/commerceMcp.types.ts (listingScope, listingGroupScope, subjectVariant)",
      "qpien-shop-v2/app/src/modules/CommerceMcp/services/commerceMcpAuth.service.ts (scopeValues)",
      "qpien-shop-v2/app/src/modules/Products/core/productListingRef.utils.ts (listingKeyOf, rankListingRefMatches)",
      "qpien-shop-v2/app/src/modules/Products/drivers/products.drizzle.driver.ts (listingKeyExpr)",
      "qpien-shop-v2/app/src/db/migrations/0022_variant_listing_key_derived.sql"
    ],
    channels: ["all", "commerce", "question"],
    position: { x: 5400, y: -1180 }
  },
  {
    id: "commerce-variant-axes",
    title: "“Hangi renkler var?” sorusu nereye gider — veriden karar verilir",
    shortTitle: "variantAxes routing",
    subtitle:
      "Kodda hiçbir eksen adı yoktur. Bedenin mi rengin mi ilan içinde olduğu MAKALEYE göre değişir.",
    kind: "decision",
    stage: "Marketplace lane",
    productSummary:
      "Trendyol'da bir babetin bedenleri tek ilanın içinde, renkleri ise ayrı ayrı ilanlarda durur. Ama bir ayakkabıda numaralar da ayrı ilanlarda olabiliyor. Yani “beden içeride, renk dışarıda” diye sabit bir kural yazmak yanlış olurdu — hangi özelliğin nerede durduğu ürüne göre değişir. Bu yüzden AI'a hazır bir kural verilmez; her ürün için katalogdan hesaplanıp söylenir.",
    details: [
      "`get_product_details` marketplace turunda `variantAxes` da döndürür: her eksen için ilan İÇİNDEKİ değerler, makale GENELİNDEKİ değerler ve tek bir karar alanı — `answerableFromThisListing`.",
      "true → soru `get_product_variants` ile ilan içinden cevaplanır. false → ilan tek değer taşıyor, diğerleri kardeş ilanlarda; `get_listing_group_variants` gerekir.",
      "Ölçülmüş dağılım (tek kiracının marketplace kataloğu): `Beden` 1093 makalede ilan içinde değişiyor ama 23 makalede SADECE ilanlar arası; `Ayakkabı Numarası` 55 makalenin hepsinde sadece ilanlar arası; `Renk` 581 makalede ilanlar arası. Sabit eşleme bu katalogda tek başına 78 makalede yanlış olurdu.",
      "Eksen adları katalogdan gelir, kodda geçmez: aynı kiracıda `Beden`, `Numara` ve `Ayakkabı Numarası` üç ayrı isimle aynı kavramı taşıyor. Kapasite, aroma, voltaj, paket adedi de aynı mekanizmayla çalışır — yeni kural gerekmez.",
      "Eksen hesabı stok/durum filtresizdir çünkü YAPISAL bir gerçektir. Filtreliyken 7 bedenli bir ilan (6'sı stokta değil) tek değer görünüyordu ve beden sorusu yanlışlıkla kardeş ilanlara yönlendiriliyordu.",
      "Maliyeti sıfırdır: host zaten her marketplace turunda `get_product_details`'i prefetch ediyor, eksenler o cevaba katıldı. `get_product_variants`/`get_product_policies` içeriden çağırdığında hesaplanmaz (`withVariantAxes`).",
      "Genişletme asla otomatik değildir — model `get_listing_group_variants`'ı çağırmak ZORUNDADIR. “Müşteri istemedikçe diğer varyantlara bakma” kuralı pratikte budur.",
      "⚠️ Değişiklik yapacaklara: buraya eksen adı yazmayın. Yeni bir platform veya yeni bir özellik ekseni geldiğinde bu mekanizma zaten çalışır; bir isim hardcode edilirse o platformda sessizce yanlış cevap üretir."
    ],
    inputs: [
      "product_variants.options (kiracının kendi sözlüğü)",
      "listingScope + listingGroupScope"
    ],
    outputs: [
      "variantAxes[].answerableFromThisListing",
      "variantAxes[].inListing / acrossListings"
    ],
    decisionOutcomes: [
      "answerableFromThisListing: true → get_product_variants (ilan içi)",
      "answerableFromThisListing: false → get_listing_group_variants (kardeş ilanlar)"
    ],
    codeRefs: [
      "qpien-shop-v2/app/src/modules/Products/drivers/products.drizzle.driver.ts (listListingOptionAxes)",
      "qpien-shop-v2/app/src/modules/Products/services/productDiscovery.service.ts (getProductDetails → variantAxes)",
      "qpien-shop-v2/app/src/modules/CommerceMcp/core/commerceMcp.schemas.ts (listingGroupOutput, variantAxes)",
      "api-v2/app/src/modules/QpienAi/services/commerceAgentRunner.service.ts (host contract)"
    ],
    channels: ["all", "commerce", "question"],
    position: { x: 6480, y: -1400 }
  },
  {
    id: "skills-authoring",
    title: "Skill yazılır ve yayınlanır",
    shortTitle: "Skill authoring",
    subtitle: "Akış şeması değil; başlıklara bölünmüş bir talimat metni.",
    kind: "skill",
    stage: "Skills authoring",
    productSummary:
      "Müşteri kendi AI prosedürünü düz metinle yazar: bir tetikleme açıklaması ve bir talimat dokümanı. Node/ok çizilmez, 'sonraki adım' seçilmez — ikinci seviye başlıklar sırayla ilerleyen adımlara dönüşür, adım içindeki mantığı (IF / ELSE IF / ELSE dahil) AI doğal dilden yorumlar.",
    details: [
      "Doküman `SKILL.md` mantığındadır: metnin başındaki bölüm genel yönerge, her ikinci seviye başlık ayrı ve sıralı bir adımdır. Başlığı olmayan doküman tek adım sayılır.",
      "Satır içi 'yetki' chip'leri bir adıma erişim verir; ok/geçiş tanımlamaz: ACTION (okuma), WRITE_ACTION (yazma), KNOWLEDGE (bilgi), ATTRIBUTE (güvenli alan okuma), UPDATE_ATTRIBUTE (alan yazma), RUN_SKILL, SWITCH_SKILL, HANDOFF_TEAM, HANDOFF_CPERSON, REPLY_VIA_DM, NO_REPLY, DELETE_COMMENT ve END.",
      "Bir Skill'in TEK bitişi vardır: `END`. Eski `RETURN_TO_QPIEN_AI` chip'i artık yazım ekranında sunulmaz; yalnız o seçenek varken yayınlanmış sürümler çalışmaya devam etsin diye korunur.",
      "`ELSE` koşul taşımaz — editör onu çıplak ekler ve arkasına koşul yazılmasını engeller; koşullu durum `ELSE IF` olmalıdır.",
      "Kapsam iki şeydir: kanal listesi ve mesaj tipi. Mesaj tipi ÜÇ kovadır — COMMENT (gönderi yorumu, marketplace ürün sorusu), TEXT_MESSAGE (sosyal DM veya normal sohbet) ve EMAIL (e-posta thread'i VE e-posta ticket'ı; `isTicket` kovayı ayırmaz). EMAIL kovasından önce e-posta TEXT_MESSAGE ile eşleşiyordu; `MIGRATE_AI_EMAIL_SKILLS` e-postaya ulaşan TEXT_MESSAGE tanımlarına EMAIL ekler. `PRIVATE_REPLY` bilinçli olarak yok: o platformun GİDEN otomatik DM tipidir ve seçenek olarak sunulduğunda tek gelen yorum iki Skill akışı başlatıyordu.",
      "Yayınlama (`publishSkill`) doküman boyutunu/derinliğini, tekil çapaları, yetki şeklini, güvenli alan yollarını, kaynakların müşteriye ait olup olmadığını, action'ın gerçekten okuma olup olmadığını ve hedef Skill'in yayınlanmış olduğunu doğrular. Çalışma zamanı HER ZAMAN yayınlanmış değişmez sürümü yükler.",
      "Rol kapsamı `roles` alanı DOKÜMAN üzerindedir, sürümde değil: bir Skill'i başka role atamak yeni yayın beklemeden anında geçerli olur. Boş liste 'tüm roller' demektir.",
      "AI destekli taslak (`generateSkillDraft`) iki modda çalışır — DRAFT (metinden taslak üret / gözden geçir) ve OPTIMIZE (mevcut taslağın ifadesini keskinleştir; değişecek şey yoksa taslağa DOKUNMAZ). İkisi de yalnız taslak döner, hiçbir şeyi yayına almaz.",
      "Üretim asla yazma (WRITE_ACTION), DELETE_COMMENT veya HANDOFF_CPERSON chip'i koymaz; bunlar editör kararıdır. Teslim işaretlerini (REPLY_VIA_DM, NO_REPLY) ise BİLEREK üretir ve optimize eder: 'cevap verme' / 'DM'den yanıtla' düz yazısı tek başına hiçbir şeyi yönlendirmez, `availableDeliveryMarkers` kapsamın izin verdiği işaretleri listeler (NO_REPLY her zaman, REPLY_VIA_DM yalnız yorum kapsamında). Optimize, editörün o anki dokümanı üzerinde çalışır (kaydedilmemiş düzenleme kaybolmaz) ve mevcut chip'leri host tarafında korur (`preserveDraftCapabilities`). Manifestte olmayan bir kaynak id'si ve kapsam dışı bir işaret uyarıyla düşürülür.",
      "`deleteSkill` sert silmedir ve iki durumda reddedilir: aktif/bekleyen bir run varken (değişmez sürümü konuşmanın ortasında kaybolur) ve başka bir Skill'in taslağı ya da yayını bu Skill'e RUN_SKILL/SWITCH_SKILL ile bağlıyken. Geçmişi saklamak için `ARCHIVED` vardır.",
      "Taslak simülasyonu (`simulateSkill`) dış tool'lar için kuru çalışmadır; semantik hafızayı ve karar izlerini döndürür."
    ],
    inputs: [
      "Trigger description",
      "Rich-text instruction document",
      "Channel + message type scope",
      "Roles"
    ],
    outputs: ["Skill draft", "Immutable published version", "Simulation trace"],
    promptNames: [
      {
        name: "SKILL_DRAFT_GENERATION",
        usage:
          "Serbest metinden Skill taslağı üretir (mode: DRAFT); yalnız taslak döner, yayına almaz."
      },
      {
        name: "SKILL_DRAFT_OPTIMIZATION",
        usage:
          "Mevcut taslağın ifadesini keskinleştirir (mode: OPTIMIZE); adım/sıra/dal/yetki yerleri korunur, gerek yoksa `alreadyOptimized` döner."
      }
    ],
    codeRefs: [
      "api-v2/app/src/modules/Skills/services/skill.service.ts",
      "api-v2/app/src/modules/Skills/services/skillGeneration.service.ts",
      "api-v2/app/src/modules/Skills/core/skill.utils.ts",
      "api-v2/app/src/modules/Skills/controller/skill.types.gql:405"
    ],
    channels: ["all", "skills"],
    position: { x: 1440, y: 1060 }
  },
  {
    id: "skills-routing",
    title: "Mesaj bir Skill'e mi ait",
    shortTitle: "Skill routing",
    subtitle: "Skills, commerce ve genel intent pipeline'ından ÖNCE sorulur.",
    kind: "skill",
    stage: "Skills routing",
    productSummary:
      "Her uygun mesaj önce Skills'e sorulur: yayınlanmış bir Skill bu mesajı sahiplenmeli mi, açık bir prosedür devam ediyor mu, yoksa mesaj normal AI akışına mı bırakılmalı?",
    details: [
      "`handleAskQuestion` içinde ilk yönlendirme burasıdır; Skill sahiplenirse commerce ve intent pipeline'ı hiç çalışmaz.",
      "Ucuz yol önce çalışır: oturum okuması + yayınlanmış aday listesi. Hiç aday yoksa ve açık run da yoksa Skills anında geri döner — hiç Skill'i olmayan müşteriye maliyeti iki Mongo okumasıdır.",
      "Aday listesi kanal + mesaj tipi + ROL kapsamına göre daraltılır: konuşma bir role oturmuşsa o rol; oturmamış ve enrich EMİNSE sınıflandırdığı rol; enrich emin değilse tüm uygun roller (bir Skill turu sahiplendiğinde pipeline kısa devre olduğu için yanlış daraltma asla düzeltilemez).",
      "Tetikleme kararı `SKILL_TRIGGER_DECISION` ile verilir ve güven eşiği 0.65'tir; eşik altı veya listede olmayan bir id 'eşleşme yok' sayılır.",
      "Aktif bir run varsa `SKILL_TURN_ROUTE` mesajı sınıflandırır: CONTINUE_ACTIVE (prosedür devam), SIDE_QUESTION (yan soru — Skill hafızası ne silinir ne ilerletilir, tur genel AI'ya gider), START_SKILL (başka Skill'e geç), CANCEL_ACTIVE (müşteri vazgeçti — yığın komple kapanır), NO_SKILL.",
      "Aynı anda tek tur: oturum Mongo lease'iyle kilitlenir (5 dakika). Kilit alınamazsa `SKILL_SESSION_BUSY` döner ve o taraf hiçbir şey göndermez.",
      "Aynı inbound mesaj iki kez işlenmez: `messageKey` işlenmiş mesaj listesinde ve son route cache'inde tutulur, webhook tekrarları aynı cevabı replay eder. Anahtar iki kovalı mesaj tipiyle üretilir (`messageId:COMMENT`), böylece bir yorum ve onun otomatik DM kardeşi aynı turun iki ayrı girişi olamaz.",
      "Skill yeniden yayınlandıysa eski sürüme çakılı run serbest bırakılır: `publishedVersionNumber` run'ın sürümüyle uyuşmuyorsa yığın iptal edilir ve mesaj güncel sürümle taze bir tur olarak yönlendirilir (`RUN_VERSION_SUPERSEDED`). Hiç yayınlanmış sürümü olmayan (duraklatılmış) Skill'e dokunulmaz.",
      "Skills `enrichUserQuery` çalıştırmaz ama enrich edilmiş mesajı GÖRÜR: tetikleme kararına ham mesaj (`currentMessage`) yanında çözümlenmiş mesaj (`resolvedMessage`) de gider. Prompt satırı hâlâ ham mesaja göre karar verdiği için bu alan toplayıcıdır; tercih ettirmek bir prompt değişikliğidir, kod değişikliği değil.",
      "Yorum turunda tetikleme kararı ve tur yönlendirmesi `postContext` (gönderi açıklaması + görsel özeti) de alır: ürün adı geçmeyen bir yorum gönderinin konusu hakkındadır, 'fiyatı ne kadar' Batman gönderisinde Batman comic skill'ini tetikler.",
      "Comment + otomatik private reply birlikte açıkken Skills iki kardeş çağrıdan değil KOORDİNATÖRDEN bir kez çalışır (kardeşler `skipSkillsRoute: true` taşır). Bu bir optimizasyon değil doğruluk şartıdır: aksi halde ikisi aynı oturum kilidi için yarışır, kaybeden taraf sessizce cevapsız kalır veya bir müşteri mesajı iki tur harcar."
    ],
    inputs: [
      "Inbound message + enrichment",
      "SkillSession (customer/person/conversation/aiProfile/channel)",
      "Published skill candidates",
      "Eligible AI roles"
    ],
    outputs: [
      "Trigger / turn route",
      "Active SkillRun",
      "continueToQpienAi",
      "SKILL_ROUTING audit step"
    ],
    decisionOutcomes: [
      "Eşleşme yok -> genel AI pipeline'ı",
      "Tetiklendi / devam -> Skill agent döngüsü",
      "SIDE_QUESTION -> genel AI pipeline'ı (Skill hafızası korunur)",
      "CANCEL_ACTIVE -> yığın kapanır",
      "Oturum meşgul / mesaj zaten işlendi -> NO_RESPONSE"
    ],
    promptNames: [
      {
        name: "SKILL_TRIGGER_DECISION",
        usage:
          "İlk turda hangi yayınlanmış Skill'in mesajı sahiplendiğini seçer (güven eşiği 0.65).",
        placeholders: [
          { token: "currentMessage", meaning: "Müşterinin ham mesajı" },
          {
            token: "resolvedMessage",
            meaning:
              "Enrich sonrası çözümlenmiş mesaj (zamirler açılmış, görsel tarif edilmiş) — opsiyonel/toplayıcı alan"
          },
          {
            token: "candidates",
            meaning: "Kanal/tip/rol kapsamına uyan Skill listesi"
          }
        ]
      },
      {
        name: "SKILL_TURN_ROUTE",
        usage:
          "Aktif bir run varken mesajı devam / yan soru / başka Skill / iptal olarak sınıflandırır."
      }
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/services/qpienAi.service.ts:1545",
      "api-v2/app/src/modules/Skills/services/skillOrchestrator.service.ts:112",
      "api-v2/app/src/modules/Skills/services/skillDecision.service.ts:29",
      "api-v2/app/src/modules/Skills/core/skill.constants.ts:28"
    ],
    channels: [
      "all",
      "skills",
      "commerce",
      "chat",
      "comment",
      "question",
      "privateReply"
    ],
    position: { x: 1800, y: 820 }
  },
  {
    id: "skills-agent",
    title: "Skill agent döngüsü çalışır",
    shortTitle: "Skill agent cycle",
    subtitle: "Model bütün dokümanı görür; aktif adımı host seçer.",
    kind: "skill",
    stage: "Skills agent",
    productSummary:
      "Her döngüde model bağlam için TÜM dokümanı ve host'un seçtiği TEK aktif adımı alır; ne yapacağını yapılandırılmış tek bir komutla söyler. Sıralı ilerlemenin sahibi host'tur, model değil.",
    details: [
      "Model her döngüde şunu döner: aktif adım id'si, adımın durumu (IN_PROGRESS / COMPLETED), müşterinin son mesajının dili ve TEK bir komut.",
      "Komutlar: RESPOND, CALL_ACTION, CALL_WRITE_ACTION, SEARCH_KNOWLEDGE, UPDATE_ATTRIBUTE, COMPLETE, HANDOFF_TEAM, HANDOFF_CPERSON, RUN_SKILL, SWITCH_SKILL, RETURN_TO_QPIEN_AI, DELETE_COMMENT, NO_RESPONSE.",
      "Yalnız AKTİF adıma ait yetkiler karar paketine konur ve her `referenceId` değişmez dokümana karşı doğrulanır. Action URL'leri, header'ları ve kimlik bilgileri modele HİÇ gönderilmez.",
      "Tool'ları yalnız host çalıştırır. Tool sonucu sınırlandırılır, kanıt olarak kaydedilir ve SONRAKİ döngüye verilir; bir döngü tool isterken aynı döngüde adımı tamamlayamaz — sonuç önce değerlendirilir.",
      "Tur başına sert sınırlar: 8 döngü ve 8 Skill atlaması.",
      "Model adım İÇİNDE herhangi bir talimata odaklanabilir ama sonraki adımı çalıştıramaz/tamamlayamaz. `COMPLETED` tam bir adım ilerletir ve tamamlanan adımlar kesintisiz bir öneke normalize edilir — ne kayıtlı state ne modelin verdiği state iş atlayabilir.",
      "Tamamlanan ama sonu olmayan adımlar AYNI müşteri turunda devam eder; üretilen müşteri metinleri tek cevapta biriktirilir. Bilgi eksikse adım IN_PROGRESS kalır, müşteriye soru sorulur (`WAITING_INPUT`) ve sonraki mesajda aynı adım devam eder.",
      "Karar doğrulaması ölümcül olanı gürültüden ayırır: adımdan kaçmaya çalışmak veya tool isteyip/soru sorup 'tamamlandı' demek turu durdurur; aktif adım dışındaki çapaları raporlamak yalnız uyarı ile düşürülür — bu, doküman tümüyle görünürken sık ve kaçınılmaz bir model kaymasıdır ve turu insan desteğine düşürmesi hiçbir şeyi korumuyordu.",
      "Skills `enrichUserQuery` çalıştırmadığı için dil tespitini agent kararı yapar: `detectedLanguage` müşterinin son mesajından okunur ve cevap talimatların dilinden bağımsız olarak o dilde yazılır. Ek LLM maliyeti yoktur.",
      "Semantik hafıza (`SkillRun.memory`) blok imleci değildir: hedef, sınırlı ilerleme özeti, host'un sahibi olduğu aktif adım, tamamlanan adım id'leri, çapalar, çıkarılan bilgiler, bekleyen müşteri isteği, son doğrulanmış tool kanıtı ve son karar gerekçesi.",
      "Bir tur bitmeden `UPDATE_ATTRIBUTE` ikinci kez sorulur: yazma, agent'ın çalıştırmadan da cevap verebileceği tek yetkidir ve koşullu dalda o dal sonraki mesajda kaybolur. Odaklanılan satırda başarılı sonucu olmayan bir yazma varsa karar iptal edilip `requiredCapability` ile yeniden alınır (çapa bazlı, yani seçilmeyen ELSE dalının yazması tetiklenmez; referans başına tur başına bir kez)."
    ],
    inputs: [
      "Rendered whole document + active step",
      "Semantic memory",
      "Active step capabilities",
      "Bounded tool evidence"
    ],
    outputs: [
      "Structured command",
      "Customer-facing text",
      "detectedLanguage",
      "SKILL_AGENT audit step"
    ],
    decisionOutcomes: [
      "RESPOND -> müşteriye metin, adım devam eder",
      "Tool komutu -> host çalıştırır, sonuç sonraki döngüde",
      "COMPLETE / HANDOFF / RETURN_TO_QPIEN_AI -> turu bitiren komut",
      "NO_RESPONSE + NO_REPLY chip'i -> tur sessiz biter, hiçbir tarafa gönderim yok",
      "Çıplak NO_RESPONSE -> agent takıldı, tur genel AI'ya devredilir"
    ],
    promptNames: [
      {
        name: "SKILL_AGENT_DECISION",
        usage:
          "Bir döngünün kararını verir: aktif adım, adım durumu, dil ve tek komut. Kanıt ve yetki manifesti payload ile gelir.",
        placeholders: [
          {
            token: "command",
            meaning:
              "İzinli komut listesi — SEARCH_KNOWLEDGE, CALL_WRITE_ACTION ve DELETE_COMMENT dahil olmalı; eski satır bu komutları HİÇ üretemez"
          },
          {
            token: "dmResponse",
            meaning:
              "Halka açık yorum cevabından ayrı DM metni (REPLY_VIA_DM); eksik satır sadece DM gönderir"
          },
          {
            token: "attributes.extra.messageScope",
            meaning:
              "Host'un COMMENT / TEXT_MESSAGE / EMAIL kararı — 'gelen mesaj bir yorum mu' talimatı buna göre yargılanır"
          }
        ]
      }
    ],
    codeRefs: [
      "api-v2/app/src/modules/Skills/services/skillAgent.service.ts:62",
      "api-v2/app/src/modules/Skills/core/skill.constants.ts:14",
      "api-v2/app/src/modules/Skills/core/skill.enums.ts",
      "api-v2/app/src/modules/Skills/core/skill.utils.ts"
    ],
    channels: ["all", "skills", "chat", "comment", "question", "privateReply"],
    position: { x: 2160, y: 820 }
  },
  {
    id: "skills-capabilities",
    title: "Yetkileri yalnız host çalıştırır",
    shortTitle: "Host-run capabilities",
    subtitle: "Okuma serbest, yazma sınırlı, kod çalıştırma yok.",
    kind: "skill",
    stage: "Skills capability",
    productSummary:
      "Model bir yetkinin ADINI seçer; çağrıyı Qpien yapar. Serbest kod çalıştırma ve modelin kontrolündeki veritabanı erişimi kapsam dışıdır.",
    details: [
      "`ACTION` müşterinin kendi GET action'ı veya GET_ORDER/GET_PRODUCT şablonudur. Okuma idempotent olduğu için serbestçe çağrılabilir — döngülerin ve tekrarların üzerine kurulu bir çalışma zamanında maliyeti yoktur.",
      "Okuma da asla patlamaz: hata `READ_FAILED`, veri gelmediyse `ACTION_DATA_UNAVAILABLE` döner. Böylece bir action hatası tüm AI run'ını düşürmek yerine yazarın kendi fallback'ini çalıştırır.",
      "`ATTRIBUTE` yalnız `person`, `conversation`, `extra` ve `aiProfile` altındaki sınırlı, güvenli yolları okur.",
      "`UPDATE_ATTRIBUTE` alan yazar ve insanın yazdığı değeri ÇÖZER: `country` herhangi bir dilde ülke adı veya iso2 kodu kabul eder (tam eşleşme; substring aramanın ilk satırı 'ran' ile France'ı seçebilirdi), `language` ISO 639-1 kodu veya İngilizce adı kabul eder. Çözülemeyen değer `INVALID_COUNTRY`/`INVALID_LANGUAGE` döner ve hiçbir şey yazılmaz; sonuç saklanan değer opak bir id olduğunda `displayValue` taşır ki agent müşteriye 'France' desin, id okumasın.",
      "`DELETE_COMMENT` modülün tek yıkıcı yetkisidir ve 'hangi mesaj' kararı tamamen host'undur: agent yalnız chip'in referansıyla komut verir, ARGÜMAN VERMEZ; host turu başlatan yorumu siler. Tur bir yorum değilse (`NOT_A_COMMENT`, host'un kendi mesaj tipi kararına göre), kanal Facebook/Instagram dışıysa (`CHANNEL_NOT_SUPPORTED` — TikTok gizler, silmez) veya simülasyondaysa platforma dokunmadan reddedilir. Silme işlemi idempotenttir: zaten silinmiş yorum ikinci platform çağrısı yapmadan başarı döner.",
      "Silme kodu `Message/services/commentModeration.service.ts` içindedir ve konuşma panelindeki silme butonu da aynı servise devreder — tek uygulama, tek davranış.",
      "Hiçbir yetki asla exception atmaz: reddedilen çağrı turu bozmaz, adımın kendi fallback'i çalışır.",
      "Modelden gelen hiçbir id, güncel yetki manifestinde olmadıkça ve müşteri sahipliği yeniden doğrulanmadıkça çalıştırılmaz. Bir chip ile bir komut asla okuma/yazma arasında geçiş yapamaz (referans-tip kontrolü).",
      "Müşteri talimatları, müşteri mesajları, etiketler ve tool sonuçları GÜVENİLMEYEN VERİ olarak ele alınır."
    ],
    inputs: [
      "Capability manifest (aktif adım)",
      "Model command + referenceId",
      "Tenant-owned resources"
    ],
    outputs: [
      "Bounded tool evidence",
      "ok:false hata kodları (READ_FAILED, ACTION_DATA_UNAVAILABLE, NOT_A_COMMENT, …)",
      "Attribute write result + displayValue"
    ],
    codeRefs: [
      "api-v2/app/src/modules/Skills/services/skillCapability.service.ts:97",
      "api-v2/app/src/modules/Skills/services/skillCapability.service.ts:244",
      "api-v2/app/src/modules/Skills/services/skillCapability.service.ts:363",
      "api-v2/app/src/modules/Message/services/commentModeration.service.ts",
      "api-v2/app/src/modules/QpienAi/core/httpAction.utils.ts"
    ],
    channels: ["all", "skills", "chat", "comment", "question", "privateReply"],
    position: { x: 2520, y: 820 }
  },
  {
    id: "skills-knowledge",
    title: "Adıma sabitlenmiş bilgi arama",
    shortTitle: "Scoped knowledge search",
    subtitle: "Ön yükleme değil; agent'ın kendi sorgusuyla çağırdığı bir tool.",
    kind: "rag",
    stage: "Skills capability",
    productSummary:
      "`KNOWLEDGE` chip'i aktif adıma yazarın seçtiği bilgiye — bir bilgi kaynağının tamamına veya içindeki tek bir dokümana — okuma izni verir. Genel RAG aşaması çalışmaz.",
    details: [
      "Agent `SEARCH_KNOWLEDGE` komutunu chip referansıyla verir ve KENDİ kendine yeten sorgusunu yazar (Skills enrich çalıştırmaz, zamirleri model çözer). Boş sorgu müşterinin ham mesajına düşer.",
      "Sonuç sonraki döngüde `{ok, query, found, chunks}` kanıtı olarak gelir; 'tool isteyen döngü adımı kapatamaz' kuralı burada da geçerli olduğu için parçalar adım kapanmadan önce okunur.",
      "Retrieval doğrudan contextual RAG V2'ye gider, asla legacy V1'e düşmez: V1'in filtre kurucusu kaynak/doküman id'lerini YOK SAYAR, yani fallback yazarın tek dokümana sabitlediğini sanırken tüm bilgi tabanını arardı. Bu yol fail-closed çalışır (`KNOWLEDGE_SEARCH_DISABLED` / `KNOWLEDGE_SEARCH_FAILED` / `KNOWLEDGE_UNAVAILABLE`) ve hepsi adımın kendi fallback'inin işlediği sıradan bir 'sonuç yok'tur.",
      "Kapsam filtreleri: doküman sabitlenmişse yalnız o satır (kapsam filtresi yok, sabitlenen satır kapsamın kendisidir); kaynağın tamamı + kapsamsız tur -> yalnız GENERAL (ürüne özel bir parça genel soruyu cevaplayamaz); kaynağın tamamı + ürün/gönderi bağlı tur -> ITEM_SPECIFIC + GENERAL iki geçiş birleştirilir (ana pipeline ile aynı şekil).",
      "Bütçe: tur başına en fazla 3 arama, arama başına en fazla 4 parça, parça başına 1200 karakter. Aynı (chip, normalize sorgu) ikilisi `DUPLICATE_SEARCH`, bütçe aşımı `SEARCH_BUDGET_EXHAUSTED` döner — ikisi de ek embedding/rerank turu ÖDEMEDEN; model önceki kanıtı hafızada görmeye devam eder ve onunla karar vermesi söylenir.",
      "Her `KNOWLEDGE` chip'i yayınlamada yeniden doğrulanır (kaynak müşteriye ait, sabitlenen doküman o kaynağa ait ve `COMPLETED`) ve aynı kontrol her aramada TEKRAR çalışır; böylece yayından sonra silinmiş bir kaynak değişmez sürüm üzerinden okunamaz."
    ],
    inputs: [
      "KNOWLEDGE chip (kaynak / doküman id)",
      "Agent'ın yazdığı sorgu",
      "Tur kapsamı (ürün/gönderi)"
    ],
    outputs: [
      "toolEvidence { ok, query, found, chunks }",
      "DUPLICATE_SEARCH / SEARCH_BUDGET_EXHAUSTED",
      "KNOWLEDGE_UNAVAILABLE"
    ],
    codeRefs: [
      "api-v2/app/src/modules/Skills/services/skillCapability.service.ts:488",
      "api-v2/app/src/modules/Skills/services/skillCapability.service.ts:540",
      "api-v2/app/src/modules/Skills/core/skill.constants.ts:18",
      "api-v2/app/src/modules/QpienAi/services/subServices/contextualRag.service.ts"
    ],
    channels: ["all", "skills", "chat", "comment", "question", "privateReply"],
    position: { x: 2880, y: 620 }
  },
  {
    id: "skills-write-action",
    title: "Dış sisteme yazma: onay + tek seferlik",
    shortTitle: "Write action gate",
    subtitle: "İlk çağrı hiçbir zaman çalıştırılmaz; müşteri onayı beklenir.",
    kind: "transaction",
    stage: "Skills write",
    productSummary:
      "`WRITE_ACTION`, müşterinin kendi POST/PUT/PATCH action'ıdır. Agent'ın kendi kararıyla ASLA çalışmaz: host ilk çağrıyı reddeder ve müşteriden açık onay alınmasını zorunlu kılar, sonra aynı çağrının ikinci kez olmasını imkânsızlaştırır.",
    details: [
      "`DELETE` yayınlamada reddedilir (`skill_write_action_method_unsupported`) — dış bir silmenin geri alması ve telafi çağrısı yoktur, hiçbir onay diyaloğu bunu güvenli yapmaz. Şablon (TEMPLATE) action'lar da reddedilir: `CREATE_NEW_ORDER` paylaşımlı bir mağaza entegrasyonu üzerinden yazar ve kendi tasarımını gerektirir. Editörün seçicisi yayınlamanın kabul ettiğiyle aynı kümeyi gösterir.",
      "Neden 'alanlar dolu' yetmez: çalışma zamanı işlemlerin tekrarlanabilir olması üzerine kurulu. Dört sıradan durum bir yetkiyi tekrarlar — aynı turun sonraki döngüsünde yeniden istemek, adım COMPLETED işaretlenmediği için sonraki mesajda aynı chip'le geri gelmek, kanıt penceresinin (son 12 kayıt) eski çağrıyı düşürmesi ve turun oturum lease'ini aşıp başka bir replica'nın oturumu devralması.",
      "ONAY KAPISI host tarafından uygulanır, promptla değil: belirli bir (referans, argüman hash'i) ikilisi için İLK `CALL_WRITE_ACTION` asla çalıştırılmaz. Teklif hafızaya (`memory.pendingWriteConfirmation`) gelen mesajın anahtarıyla yazılır ve agent'a `ok:false, CONFIRMATION_REQUIRED` + tam parametreler döner. Çağrı ancak aynı argümanlar FARKLI bir mesaj anahtarı taşıyan bir turda geri geldiğinde çalışır — yani müşteriye değerler gösterilmiş ve o cevap vermiş.",
      "Host'un garanti ettiği şey tur sınırıdır, cevabın anlamı değil: 'evet'i okumak agent'ın işidir. Host'un imkânsız kıldığı şey ise promptun yapamayacağı kısımdır — agent teklifi atlayamaz (teklifi host yazar), kendi kendini onaylayamaz (mesaj anahtarı host'un) ve onaydan sonra farklı değerler geçiremez (farklı hash yeni bir tur açar).",
      "TEK SEFERLİK garantisi `SkillActionExecutions` kaydıyla verilir: istek gitmeden ÖNCE `IN_FLIGHT` satırı (customer, run, referans, argüman hash'i) üzerinde tekil indeksle yazılır — insert'in kendisi sahiplenmedir, yani 4 api-v2 replica'sı arasında yalnız bir çağıran o yazmanın sahibidir.",
      "Kaybeden çağıran bulduğu satıra göre cevap alır: `COMPLETED` kayıtlı cevabı replay eder (`alreadyExecuted: true` + ORİJİNAL sonuç, hata zarfı dahil — agent ilk okuduğunu okur, uydurma bir başarı değil), `IN_FLIGHT` `WRITE_IN_PROGRESS`, `FAILED` `WRITE_ALREADY_ATTEMPTED` döner.",
      "Oturum lease'inden eski bir `IN_FLIGHT` satırı, isteğin ortasında ölmüş bir süreçten kalmış sayılır ve `WRITE_ALREADY_ATTEMPTED` döner; bu olmadan sahiplenme ile sonuç arasındaki bir çökme o yazmayı run boyunca 'çalışıyor' diye çakılı bırakırdı.",
      "BAŞARISIZ deneme bilinçli olarak nihaidir: zaman aşımına düşen bir istek karşı tarafta uygulanmış olabilir, bu yüzden aynı çağrı otomatik tekrar edilmez. Düzeltilmiş değerler farklı hash üretir ve çalışmakta serbesttir.",
      "Argüman yerleştirme JSON farkındadır: string içindeki `{{name}}` JSON-escape edilir, dışındaki aynen basılır. Bu olmadan tırnak veya satır sonu taşıyan bir LLM değeri karşı tarafın reddettiği bir gövde üretiyordu. Bu düzeltme paylaşımlı HTTP yolunda olduğu için sıradan Qpien AI action'larını da düzeltir. URL hâlâ ham yerleştirilir (kendi encode'unu yapan action'ları çift encode etmemek için).",
      "Run'a bağlanamayan bir çağrı (yazım simülatörü veya `skillRunId` olmayan çağıran) korumasız çalışmak yerine HİÇ çalışmaz: `WRITE_SCOPE_UNAVAILABLE`."
    ],
    inputs: [
      "WRITE_ACTION chip",
      "Model arguments (hash)",
      "Inbound messageKey",
      "SkillActionExecutions kaydı"
    ],
    outputs: [
      "CONFIRMATION_REQUIRED + parametreler",
      "Tam olarak bir kez çalışan HTTP isteği",
      "WRITE_IN_PROGRESS / WRITE_ALREADY_ATTEMPTED / WRITE_SCOPE_UNAVAILABLE"
    ],
    decisionOutcomes: [
      "İlk çağrı -> çalıştırılmaz, müşteriden onay istenir",
      "Farklı mesaj anahtarı + aynı argüman -> çalışır",
      "Değerler değişti -> yeni hash, yeni onay turu",
      "Yarışı kaybeden çağıran -> kayıtlı sonuç veya reddetme"
    ],
    codeRefs: [
      "api-v2/app/src/modules/Skills/services/skillAgent.service.ts:475",
      "api-v2/app/src/modules/Skills/services/skillCapability.service.ts:688",
      "api-v2/app/src/modules/Skills/drivers/skillRuntime.mongo.driver.ts",
      "api-v2/app/src/modules/Skills/core/skill.constants.ts:78"
    ],
    channels: ["all", "skills", "chat", "comment", "question", "privateReply"],
    position: { x: 2880, y: 1060 }
  },
  {
    id: "skills-orchestration",
    title: "Skill yığını, bütçeler ve devir",
    shortTitle: "Stack + budgets",
    subtitle: "Alt Skill, geçiş, insana devir ve takılma yönetimi.",
    kind: "skill",
    stage: "Skills orchestration",
    productSummary:
      "Bir Skill başka bir Skill'i çalıştırabilir, başka birine geçebilir, insana devredebilir veya turu genel AI'ya bırakabilir. Bütün bu geçişlerin ve bütçelerin sahibi orchestrator'dır.",
    details: [
      "`RUN_SKILL` ebeveyni LIFO yığına askıya alır, çocuğu çalıştırır ve çocuk `COMPLETE` olduğunda ebeveyni çocuğun özetiyle (`memory.lastChildSkillResult`) devam ettirir. Bu, prompt'un işaret edebilmesi için skill id'sine göre bir fact değil, adı belli bir alandır.",
      "`SWITCH_SKILL`, açık iptal ve insana devir TÜM askıdaki yığını kapatır. `RETURN_TO_QPIEN_AI` kapatmaz: yalnız kendi run'ını tamamlar, askıdaki ebeveyni devam ettirir ve bu turu genel AI pipeline'ına bırakır — çekilen bir çocuk, müşterinin ortasında olduğu prosedürü yok etmemelidir.",
      "`CANCEL_ACTIVE` bir seviye geri değil yığını kapatır: müşteri işi bıraktı, işin bir katmanını değil.",
      "Bir alt Skill referansı tur boyunca TÜKENİR: yönlendirilen referans id'leri tur durumunda tutulur ve o chip karar paketinden çekilir — böylece `lastChildSkillResult` ile dönen ebeveyn az önce geldiği çocuğa tekrar giremez ve o çocuğun action'larını tekrarlamaz. Atlama sayacı bunu tek başına kapatamaz: aynı çocuğu isteyen bir ebeveyn 8 atlamanın hepsini harcayıp turu insan desteğinde bitirirdi. Tetiklenirse audit'e `SUB_SKILL_ALREADY_ROUTED` yazılır.",
      "`RUN_SKILL` isteyen bir döngü aynı anda adımını tamamlayamaz: chip'in sahibi adım, çocuk döndüğünde biter — erken tamamlamak ebeveyni yazarın yazdığından bir adım ileride devam ettirirdi. Bu, turu iptal etmek yerine uyarıyla IN_PROGRESS'e indirilir.",
      "`SWITCH_SKILL` tur sayacını devraldığı Skill'e TAŞIR: müşteri aynı konuşmadadır, bütçe onu takip eder; sıfırdan sayan iki Skill birbirine geçerek bütçeyi sonsuza kadar yenileyebilirdi.",
      "`SKILL_MAX_SUSPENDED_RUNS` derinlik sınırıyla düşen bir run çıkışta iptal edilir; aksi halde sessizce kesilmiş id `SUSPENDED` kalır ve onu tutan hiçbir şey olmaz.",
      "İnsana devir gerçekleşmezse (bilinmeyen takım, çevrimiçi üye yok, turda konuşma yok) sessizce 'başarılı' sayılmaz: run yine biter ama cevap `aiAction: 'humanSupport'` ile döner ve takım devrinde `handoffTeamId` taşınır, böylece platformun kendi eskalasyonu konuşmayı sahiplenir. Başarısızlık audit'e `source: 'skills.handoff'` ile yazılır.",
      "`SKILL_MAX_TURNS_PER_RUN` (20) bir run'ın kaç müşteri turu tüketebileceğini sınırlar: döngü ve atlama sınırları hiç tamamlanmayan bir run'ı durduramaz, o run 30 günlük oturum TTL'ine kadar konuşmayı tutardı. Bütçe tükendiğinde yığın iptal edilir, audit'e `RUN_BUDGET_EXHAUSTED` yazılır ve tur genel AI pipeline'ına düşer.",
      "TAKILAN TUR yutulmaz: agent'ın `NO_RESPONSE` komutu artık turu sessizce bitirmiyor. Sonrası olan `COMPLETED` adım döngüyü sürdürür, sonrası olmayan `COMPLETE`'e katlanır — dolayısıyla buraya ulaşan her `NO_RESPONSE` agent'ın gerçekten takıldığı anlamına gelir (tipik olarak aktif adımda yazarın koymadığı bir yetki gerekiyordur, model bunu `rationale` alanında söyler). Sonuç `{ handled: false, continueToQpienAi: true }` ve `RUN_STALLED` kaydıdır: genel pipeline turu cevaplar, run sonraki mesaj için açık kalır. TEK İSTİSNA `NO_REPLY` chip'i: agent'ın davrandığı satır bu işareti taşıyorsa sessizlik yazarındır — tur `NO_RESPONSE` politikasıyla `handled: true` biter, hiçbir tarafa gönderim olmaz, audit'e `decision: 'NO_REPLY'` yazılır. Silinemeyen bir yorumun ELSE dalı bu chip olmadan genel pipeline'a düşüp hem yorum hem DM üretiyordu (run 6a9eb5496b6987992c805c53).",
      "`END` host tarafından uygulanır: agent `END` taşıyan bir talimatı tamamlandı işaretleyip yine de nihai cevap komutu döndürürse, çalışma zamanı bu komutu bildirilen `END` referansıyla `COMPLETE`'e çözer.",
      "`SkillSession` (customer, person, conversation, aiProfile, channel) 30 gün sonra düşer."
    ],
    inputs: [
      "Terminal / routing command",
      "Suspended run stack",
      "turnCount + hop counter",
      "Handoff target"
    ],
    outputs: [
      "Turn answer",
      "Suspend / resume / cancel",
      "continueToQpienAi",
      "RUN_STALLED / RUN_BUDGET_EXHAUSTED / SUB_SKILL_ALREADY_ROUTED"
    ],
    decisionOutcomes: [
      "COMPLETE -> run biter, ebeveyn varsa devam eder",
      "RUN_SKILL -> ebeveyn askıya alınır, çocuk çalışır",
      "SWITCH_SKILL / CANCEL / handoff -> yığın kapanır",
      "RETURN_TO_QPIEN_AI / çıplak NO_RESPONSE -> tur genel AI'ya devredilir",
      "NO_RESPONSE + NO_REPLY -> handled, gönderim yok"
    ],
    codeRefs: [
      "api-v2/app/src/modules/Skills/services/skillOrchestrator.service.ts:366",
      "api-v2/app/src/modules/Skills/services/skillOrchestrator.service.ts:396",
      "api-v2/app/src/modules/Skills/services/skillOrchestrator.service.ts:605",
      "api-v2/app/src/modules/Skills/services/skillHandoff.service.ts",
      "api-v2/app/src/modules/Skills/core/skill.constants.ts:22"
    ],
    channels: ["all", "skills", "chat", "comment", "question", "privateReply"],
    position: { x: 3240, y: 820 }
  },
  {
    id: "skills-delivery",
    title: "Skill cevabı nereye gider",
    shortTitle: "Skill delivery",
    subtitle: "Tur başına tek cevap; hedefini Skill'in kendisi söyler.",
    kind: "output",
    stage: "Skills output",
    productSummary:
      "Bir Skill tur başına TEK cevap üretir ve artık kendi hedefini de adlandırır: yorum halka açık cevaplanır, sohbet mesajı sohbette cevaplanır, `REPLY_VIA_DM` ise tek istisnadır.",
    details: [
      "Müşteriye giden metni olan Skill cevabı (`responsePolicy: 'STANDARD_FINALIZE'`) RAG/action/commerce cevaplarıyla AYNI `FINALIZE_AI_CONTENT` adımından geçer; böylece müşterinin ton, cevap uzunluğu, iletişim tarzı ve kendini tanıtma kuralları uygulanır — Skill'in ham metni olduğu gibi gönderilmez.",
      "Yalnız içeriksiz rotalar finalize'ı atlar: `NO_RESPONSE` (oturum meşgul, mesaj zaten işlendi) ve metin yazmamış çıplak `humanSupport`.",
      "`aiAction` gönderim yolu seçicisidir ve Skill hangi aileye aitse onun kuralını izler: comment/question/private reply tarafında `positive`, chat tarafında BOŞ. Chat turunda `positive` demek cevabı yorum yoluna sokuyordu; o yolun `chatSettings` dalı yalnız müşteri `aiReplyButtons.aiHelped.actionMessage` alanını doldurmuşsa gönderir (varsayılan boş), dolayısıyla cevap hatasız/logsuz düşerken kapanış etiketi, notu ve anketi çalışıyor ve konuşma müşteri hiç cevap almadan kapanıyordu.",
      "`REPLY_VIA_DM` bir komut ya da tool değil, TEK bir talimat satırındaki teslim işaretidir: 'bu satırdan bir DM gider'. Yanında halka açık bir yorum cevabı gidip gitmeyeceği yazarın satırda ne yazdığına ve agent'ın halka açık metin de yazıp yazmadığına bağlıdır (üç durum: yalnız yorum / yorum + DM, ayrı metinler / yalnız DM).",
      "`NO_REPLY` aynı mekanizmanın sessizlik ikizidir: 'bu satırdan hiçbir şey gitmez'. Aynı çapa okumasıyla host uygular, her kapsamda geçerlidir (yorum, sohbet) ve varsa yazılmış metni de düşürür; agent'ın çıplak `NO_RESPONSE` komutu ise takılma sayılır.",
      "Skill metni finalize'da `skill_authored` bayrağıyla gider: FINALIZE_AI_CONTENT'in 'yardımcı olamıyorum' fallback'i (kural 6) ve tek-seferlik kanalın 'destek teklif etme' kuralı yazarın kendi cümlesini ezemez; yalnız dil/ton uygulanır. Pazaryeri uyum kapısı yalnız pazaryeri platformlarındaki (Trendyol, Hepsiburada, N11, Amazon…) yayınlanan cevaplara bakar — Instagram yorumu ve DM kapsam dışıdır — ve Skill cevabında sayıları yazarın kendi dokümanına (`complianceEvidence`) karşı doğrular, `skill:<id>` kaynağına karşı değil.",
      "İşaret, agent'ın TOOL OLMAYAN bir döngüde bildirdiği çapalardan okunur — böylece IF/ELSE'i takip eder: agent hangi dalda davrandıysa o dalın çapalarını adlandırır, IF dalındaki işaret ELSE seçildiğinde tetiklenmez. Tool döngüleri dışlanır ki dal seçilmeden önce yapılmış bir sorgu hedefi belirlemesin.",
      "Sohbet turunda işaret düşürülür ve yalnız-DM metni halka açık metne katlanır; bu katlama olmadan yorum için yazılmış bir dal sohbet müşterisine hiçbir şey cevaplamazdı.",
      "Yalnız gelen bir YORUMUN özel tarafı vardır: sohbetin normal cevabı zaten özeldir, marketplace sorusunun DM kanalı yoktur — bu iki durumda işaret sessizce hiçbir şey yapmaz. Kapsam yorumları dışlıyorsa chip yayınlamada reddedilir ve kapsam daraltıldığında dokümandaki chip'ler temizlenir.",
      "`aiProfile.commentSettings.sendPrivateReply` bir SKILL cevabı için artık hiçbir şeye karar vermez: o ayar genel pipeline'da tek cevabı iki tarafa çoğaltır, Skill yazarı ise dal başına her tarafın ne diyeceğini yazar. İşaretsiz cevap asla çoğaltılmaz, işaretli cevap asla tek tarafa katlanmaz. Ayar, Skill olmayan her tur için aynen çalışır.",
      "Tenant tarafında konuşma panelindeki 'Skill akışı' kartı `getSkillRunView` ile beslenir: yazarın kendi talimat satırları, her adımda AI'ın ne yaptığı ve adımın nasıl bittiği gösterilir — çapa, referans id, komut, prompt, token ve maliyet bilinçli olarak taşınmaz; onlar admin AI log'unda kalır."
    ],
    inputs: [
      "Skill turn text",
      "REPLY_VIA_DM işareti + dmResponse",
      "messageScope (COMMENT / TEXT_MESSAGE / EMAIL)"
    ],
    outputs: [
      "responsePolicy (STANDARD_FINALIZE / NO_RESPONSE)",
      "aiAction (positive / boş / humanSupport)",
      "replyViaDm + dmContent",
      "getSkillRunView kartı"
    ],
    decisionOutcomes: [
      "Metin var -> STANDARD_FINALIZE -> FINALIZE_AI_CONTENT",
      "Yorum + DM -> iki ayrı metin, tek tur",
      "Yalnız DM -> gönderim tipi PRIVATE_REPLY'a çevrilir",
      "NO_RESPONSE / metinsiz humanSupport -> gönderim yok"
    ],
    codeRefs: [
      "api-v2/app/src/modules/Skills/services/skillOrchestrator.service.ts:1237",
      "api-v2/app/src/modules/Skills/services/skillAgent.service.ts",
      "api-v2/app/src/modules/QpienAi/services/qpienAi.service.ts:1717",
      "api-v2/app/src/modules/Message/services/platform.service.ts"
    ],
    channels: ["all", "skills", "chat", "comment", "question", "privateReply"],
    position: { x: 3600, y: 820 }
  },
  {
    id: "skills-audit",
    title: "Skill adımları izlenir",
    shortTitle: "Skill audit trail",
    subtitle: "Aynı run zaman çizelgesinde, okunabilir kelimelerle.",
    kind: "audit",
    stage: "Observability",
    productSummary:
      "Bir mesajın Skill'e girip girmediği, hangi Skill run'ına bağlandığı, hangi komutun verildiği ve hangi talimat satırlarında çalışıldığı admin AI log'unda sıradan AI adımlarının yanında görünür.",
    details: [
      "Paylaşımlı AI zaman çizelgesine `SKILL_ROUTING` (tetikleme/tur yönlendirme kararı ve her Skill çalıştırmasından önce) ve döngü başına bir `SKILL_AGENT` adımı yazılır; her tur o turun inbound `AiMessages` run'ına bağlanır — yalnız ilk mesaja değil.",
      "Her `SkillRun` inbound `AiMessages` run'ına `aiMessageId` ile bağlıdır. Her `SkillRunStep` döngü, komut, odaklanılan çapalar, gerekçe, sınırlı girdi/çıktı ve süreyi kaydeder.",
      "`SKILL_AGENT` adımı ayrıca `response.skillContext` taşır: id'leri çalıştığı DEĞİŞMEZ sürüme karşı çözer — Skill adı, aktif adımın başlığı ve kaçıncı adım olduğu, üzerinde çalışılan talimat satırlarının METNİ ve kullanılan yetkinin adı. Çıplak id'ler aylar sonra okunamaz ve işaret ettikleri sürüm admin panelinde elde değildir, bu yüzden kelimeler audit anında yazılır.",
      "Gerçek bir LLM çağrısı olan her adım `durationMs` ve `{inputToken, outputToken, amount}` kullanım bilgisi taşır; böylece Skill adımları run'ın token/maliyet/süre toplamlarına diğer adımlar gibi katılır — öncesinde sessizce sıfır sayılıyorlardı. LLM çağrısı olmayan işaret adımları (Skill'e girildi kaydı) bilinçli olarak kullanım taşımaz.",
      "Yönlendirme kararları da izde adıyla görünür: `RUN_STALLED`, `RUN_BUDGET_EXHAUSTED`, `RUN_VERSION_SUPERSEDED`, `SUB_SKILL_ALREADY_ROUTED`.",
      "Simülasyon çıktısında `command` her zaman döner (turun sonucudur ve yazım simülatörü bunu tenant'a düz cümleyle anlatır); `decision`, `selectedSkillId` ve `debug` alanları izin kontrolü arkasındadır."
    ],
    inputs: [
      "Run id + aiMessageId",
      "Decision payload / rationale",
      "Immutable skill version",
      "LLM usage metadata"
    ],
    outputs: [
      "SKILL_ROUTING / SKILL_AGENT adımları",
      "SkillRun + SkillRunStep kayıtları",
      "Token / maliyet / süre toplamları"
    ],
    codeRefs: [
      "api-v2/app/src/modules/QpienAi/core/aiProfile.enums.ts:76",
      "api-v2/app/src/modules/Skills/services/skillOrchestrator.service.ts",
      "api-v2/app/src/modules/Skills/drivers/skillRuntime.mongo.driver.ts",
      "api-v2/app/src/modules/QpienAi/services/aiAuditService.service.ts"
    ],
    channels: ["all", "skills"],
    position: { x: 2160, y: 1240 }
  }
]

export const AI_FLOW_EDGES: AiFlowEdgeInfo[] = [
  { id: "e1", source: "incoming-message", target: "profile-memory" },
  { id: "e2", source: "profile-memory", target: "start-routing" },
  {
    id: "e3",
    source: "start-routing",
    target: "question-product",
    label: "QUESTION",
    channels: ["all", "question"]
  },
  {
    id: "e4",
    source: "question-product",
    target: "handle-ask-question",
    channels: ["all", "question"]
  },
  {
    id: "e5",
    source: "start-routing",
    target: "handle-ask-question",
    label: "Chat / comment / private"
  },
  {
    id: "e6",
    source: "handle-ask-question",
    target: "enrich",
    label: "turun ilk adımı: enrichment",
    animated: true
  },

  // ---------- Skills lane ----------
  {
    id: "skills-e1",
    source: "enrich",
    target: "skills-routing",
    label: "Skills yönlendirmesi (resolvedMessage + rol)",
    animated: true
  },
  {
    id: "skills-e2",
    source: "skills-authoring",
    target: "skills-routing",
    label: "yayınlanmış sürüm",
    channels: ["all", "skills"]
  },
  {
    id: "skills-e3",
    source: "skills-routing",
    target: "commerce-workflow-gateway",
    label: "Skill sahiplenmedi"
  },
  {
    id: "skills-e4",
    source: "skills-routing",
    target: "skills-agent",
    label: "tetiklendi / devam ediyor",
    channels: ["all", "skills"],
    animated: true
  },
  {
    id: "skills-e5",
    source: "skills-agent",
    target: "skills-capabilities",
    label: "tool komutu",
    channels: ["all", "skills"],
    animated: true
  },
  {
    id: "skills-e6",
    source: "skills-capabilities",
    target: "skills-agent",
    label: "tool kanıtı (sonraki döngü)",
    channels: ["all", "skills"],
    animated: true
  },
  {
    id: "skills-e7",
    source: "skills-capabilities",
    target: "skills-knowledge",
    label: "SEARCH_KNOWLEDGE",
    channels: ["all", "skills"]
  },
  {
    id: "skills-e8",
    source: "skills-knowledge",
    target: "skills-agent",
    label: "chunk kanıtı",
    channels: ["all", "skills"]
  },
  {
    id: "skills-e9",
    source: "skills-capabilities",
    target: "skills-write-action",
    label: "CALL_WRITE_ACTION",
    channels: ["all", "skills"]
  },
  {
    id: "skills-e10",
    source: "skills-write-action",
    target: "skills-agent",
    label: "CONFIRMATION_REQUIRED / sonuç",
    channels: ["all", "skills"]
  },
  {
    id: "skills-e11",
    source: "skills-agent",
    target: "skills-orchestration",
    label: "turu bitiren komut",
    channels: ["all", "skills"]
  },
  {
    id: "skills-e12",
    source: "skills-orchestration",
    target: "skills-agent",
    label: "RUN_SKILL / SWITCH_SKILL (atlama)",
    channels: ["all", "skills"]
  },
  {
    id: "skills-e13",
    source: "skills-orchestration",
    target: "skills-delivery",
    label: "tur cevabı",
    channels: ["all", "skills"]
  },
  {
    id: "skills-e14",
    source: "skills-orchestration",
    target: "commerce-workflow-gateway",
    label: "RETURN_TO_QPIEN_AI / yan soru / RUN_STALLED"
  },
  {
    id: "skills-e15",
    source: "skills-delivery",
    target: "finalize",
    label: "STANDARD_FINALIZE",
    animated: true
  },
  {
    id: "skills-e16",
    source: "skills-delivery",
    target: "send-and-memory",
    label: "NO_RESPONSE / metinsiz humanSupport"
  },
  {
    id: "skills-e17",
    source: "skills-routing",
    target: "skills-audit",
    label: "SKILL_ROUTING",
    channels: ["all", "skills"]
  },
  {
    id: "skills-e18",
    source: "skills-agent",
    target: "skills-audit",
    label: "SKILL_AGENT",
    channels: ["all", "skills"]
  },

  // ---------- commerce entry ----------
  {
    id: "commerce-e1",
    source: "commerce-workflow-gateway",
    target: "guard",
    label: "aktif workflow yok"
  },
  {
    id: "commerce-e2",
    source: "commerce-workflow-gateway",
    target: "commerce-continuation",
    label: "aktif workflow",
    channels: ["commerce"],
    animated: true
  },

  // ---------- generic pipeline ----------
  { id: "e7", source: "guard", target: "role-resolution", label: "continue" },
  { id: "e8", source: "role-resolution", target: "guidance" },
  { id: "e9", source: "guidance", target: "intent" },
  { id: "e10", source: "intent", target: "intent-decision" },
  {
    id: "e11",
    source: "intent-decision",
    target: "intent-rag-action",
    label: "ragAndAction",
    animated: true
  },
  {
    id: "e12",
    source: "intent-rag-action",
    target: "intent-rag",
    label: "RAG branch"
  },
  {
    id: "e13",
    source: "intent-rag-action",
    target: "intent-action",
    label: "Action branch"
  },
  {
    id: "e14",
    source: "intent-decision",
    target: "intent-rag",
    label: "rag"
  },
  {
    id: "e15",
    source: "intent-decision",
    target: "intent-action",
    label: "action"
  },
  {
    id: "e16",
    source: "intent-decision",
    target: "intent-direct",
    label: "basic / helped / clarify"
  },
  {
    id: "e17",
    source: "intent-decision",
    target: "intent-human",
    label: "humanSupport"
  },
  {
    id: "e18",
    source: "intent-decision",
    target: "intent-spam",
    label: "spam"
  },
  {
    id: "commerce-e3",
    source: "intent-decision",
    target: "commerce-orchestrator",
    label: "commerce (ilk mesaj)",
    channels: ["commerce"],
    animated: true
  },
  {
    id: "commerce-e28",
    source: "intent-decision",
    target: "intent-rag",
    label: "commerce ile paralel RAG (ilk mesaj)",
    channels: ["commerce"],
    animated: true
  },
  {
    id: "e19",
    source: "intent-rag",
    target: "rag-retrieval"
  },
  {
    id: "e20",
    source: "rag-retrieval",
    target: "rag-answer"
  },
  {
    id: "e21",
    source: "intent-action",
    target: "action-detection"
  },
  {
    id: "e22",
    source: "action-detection",
    target: "action-request",
    label: "tool match",
    animated: true
  },
  { id: "e23", source: "action-request", target: "action-answer" },
  {
    id: "e24",
    source: "action-detection",
    target: "rag-retrieval",
    label: "no tool fallback"
  },
  {
    id: "e25",
    source: "rag-answer",
    target: "rag-action-merge",
    label: "parallel result",
    animated: true
  },
  {
    id: "e26",
    source: "action-answer",
    target: "rag-action-merge",
    label: "parallel result",
    animated: true
  },
  {
    id: "e27",
    source: "intent-direct",
    target: "direct-outcome"
  },
  {
    id: "e28",
    source: "direct-outcome",
    target: "finalize",
    label: "finalize"
  },
  { id: "e29", source: "rag-answer", target: "finalize", label: "answer" },
  { id: "e30", source: "action-answer", target: "finalize", label: "answer" },
  { id: "e31", source: "rag-action-merge", target: "finalize" },
  { id: "e32", source: "finalize", target: "send-and-memory" },
  {
    id: "e33",
    source: "intent-human",
    target: "send-and-memory",
    label: "support action"
  },
  {
    id: "e34",
    source: "intent-spam",
    target: "send-and-memory",
    label: "spam action"
  },
  {
    id: "e35",
    source: "send-and-memory",
    target: "private-reply",
    label: "comment DM enabled",
    channels: ["all", "comment", "privateReply"]
  },
  {
    id: "e36",
    source: "close-conversation",
    target: "finalize",
    label: "auto-close",
    animated: true
  },

  // ---------- commerce workflow ----------
  {
    id: "commerce-e4",
    source: "commerce-continuation",
    target: "commerce-orchestrator",
    label: "expected / side / resume",
    channels: ["commerce"]
  },
  {
    id: "commerce-e5",
    source: "commerce-continuation",
    target: "guard",
    label: "NEW_TOPIC -> suspend",
    channels: ["commerce"]
  },
  {
    id: "commerce-e32",
    source: "commerce-orchestrator",
    target: "commerce-turn-context",
    label: "turun konusu çözümlenir",
    channels: ["commerce"]
  },
  {
    id: "commerce-e33",
    source: "commerce-turn-context",
    target: "commerce-mcp-client",
    label: "resolve_product_by_url (host-only)",
    channels: ["commerce"]
  },
  {
    id: "commerce-e34",
    source: "commerce-turn-context",
    target: "commerce-agent",
    label: "güvenilir ürün + konu ipucu",
    channels: ["commerce"],
    animated: true
  },
  {
    id: "commerce-e35",
    source: "commerce-turn-context",
    target: "commerce-marketplace-lane",
    label: "msgType QUESTION → marketplace lane",
    channels: ["commerce"],
    animated: true
  },
  {
    id: "commerce-e36",
    source: "commerce-marketplace-lane",
    target: "commerce-listing-scope",
    label: "ilan çözüldü → kapsam kilitlenir",
    channels: ["commerce"],
    animated: true
  },
  {
    id: "commerce-e37",
    source: "commerce-marketplace-lane",
    target: "commerce-rag-merge",
    label: "ilan çözülemedi → commerce atlanır (fail-closed)",
    channels: ["commerce"]
  },
  {
    id: "commerce-e38",
    source: "commerce-listing-scope",
    target: "commerce-mcp-client",
    label: "shop / listing / listingGroup / subjectVariant header'ları",
    channels: ["commerce"],
    animated: true
  },
  {
    id: "commerce-e39",
    source: "commerce-read-tools",
    target: "commerce-variant-axes",
    label: "get_product_details → variantAxes",
    channels: ["commerce"]
  },
  {
    id: "commerce-e40",
    source: "commerce-variant-axes",
    target: "commerce-agent",
    label: "hangi eksen nerede cevaplanır",
    channels: ["commerce"],
    animated: true
  },
  {
    id: "commerce-e6",
    source: "commerce-orchestrator",
    target: "commerce-slot-state",
    label: "CAS transition",
    channels: ["commerce"]
  },
  {
    id: "commerce-e7",
    source: "commerce-slot-state",
    target: "commerce-agent",
    label: "discovery / read",
    channels: ["commerce"]
  },
  {
    id: "commerce-e8",
    source: "commerce-agent",
    target: "commerce-mcp-client",
    label: "izinli tool çağrısı",
    channels: ["commerce"],
    animated: true
  },
  {
    id: "commerce-e9",
    source: "commerce-slot-state",
    target: "commerce-mcp-client",
    label: "prepare host call",
    channels: ["commerce"]
  },
  {
    id: "commerce-e10",
    source: "commerce-approval",
    target: "commerce-mcp-client",
    label: "onaylanmış write",
    channels: ["commerce"],
    animated: true
  },
  {
    id: "commerce-e11",
    source: "commerce-reconciliation",
    target: "commerce-mcp-client",
    label: "operation status",
    channels: ["commerce"]
  },
  {
    id: "commerce-e12",
    source: "commerce-mcp-client",
    target: "commerce-mcp-server",
    label: "özel ağ üzerinden MCP isteği",
    channels: ["commerce"],
    animated: true
  },
  {
    id: "commerce-e13",
    source: "commerce-mcp-server",
    target: "commerce-read-tools",
    label: "read yetkisi",
    channels: ["commerce"]
  },
  {
    id: "commerce-e14",
    source: "commerce-read-tools",
    target: "commerce-agent",
    label: "güvenilmeyen tool sonucu",
    channels: ["commerce"],
    animated: true
  },
  {
    id: "commerce-e15",
    source: "commerce-mcp-server",
    target: "commerce-prepare",
    label: "prepare proposal",
    channels: ["commerce"]
  },
  {
    id: "commerce-e16",
    source: "commerce-prepare",
    target: "commerce-approval",
    label: "WAITING_APPROVAL",
    channels: ["commerce"]
  },
  {
    id: "commerce-e17",
    source: "commerce-mcp-server",
    target: "commerce-write",
    label: "place / cancel",
    channels: ["commerce"]
  },
  {
    id: "commerce-e18",
    source: "commerce-write",
    target: "commerce-reconciliation",
    label: "belirsiz sonuç / timeout",
    channels: ["commerce"]
  },
  {
    id: "commerce-e19",
    source: "commerce-agent",
    target: "commerce-response-policy",
    label: "discovery answer",
    channels: ["commerce"]
  },
  {
    id: "commerce-e20",
    source: "commerce-slot-state",
    target: "commerce-response-policy",
    label: "missing input",
    channels: ["commerce"]
  },
  {
    id: "commerce-e21",
    source: "commerce-approval",
    target: "commerce-response-policy",
    label: "approval prompt",
    channels: ["commerce"]
  },
  {
    id: "commerce-e22",
    source: "commerce-write",
    target: "commerce-response-policy",
    label: "transaction result",
    channels: ["commerce"]
  },
  {
    id: "commerce-e23",
    source: "commerce-reconciliation",
    target: "commerce-response-policy",
    label: "reconciled result",
    channels: ["commerce"]
  },
  {
    id: "commerce-e24",
    source: "commerce-response-policy",
    target: "finalize",
    label: "STANDARD_FINALIZE (devam mesajı)",
    channels: ["commerce"]
  },
  {
    id: "commerce-e25",
    source: "commerce-response-policy",
    target: "send-and-memory",
    label: "PRESERVE_EXACT",
    channels: ["commerce"]
  },
  {
    id: "commerce-e29",
    source: "commerce-response-policy",
    target: "commerce-rag-merge",
    label: "STANDARD_FINALIZE (ilk mesaj, RAG ile paralel)",
    channels: ["commerce"]
  },
  {
    id: "commerce-e30",
    source: "rag-answer",
    target: "commerce-rag-merge",
    label: "paralel RAG sonucu",
    channels: ["commerce"],
    animated: true
  },
  {
    id: "commerce-e31",
    source: "commerce-rag-merge",
    target: "finalize",
    label: "birleştirilmiş / tek cevap",
    channels: ["commerce"]
  },
  {
    id: "commerce-e26",
    source: "commerce-orchestrator",
    target: "commerce-audit",
    label: "workflow steps",
    channels: ["commerce"]
  },
  {
    id: "commerce-e27",
    source: "commerce-mcp-client",
    target: "commerce-audit",
    label: "agent + tool steps",
    channels: ["commerce"]
  }
]
